/**
 *  @class
 *  @function ScrollVideoSplit
 *
 *  Two-column video section: the left column holds one tall video whose
 *  playback progress is driven by normal page scroll (no sticky), the right
 *  column holds N loop videos that restart from 0 whenever they become at
 *  least `data-threshold` percent visible. Shares the loading/seek engine
 *  with scroll-video via ScrollVideoCore.
 */
/**
 * Which scroll threshold the play gate uses.
 *
 * On first entry the caption track must first pull out from the phone's right edge (time-driven, see triggerCaptionReveal);
 * until the pull-out finishes the gate is **shut tight** (Infinity) — the pull-out isn't a scroll position, so no threshold can be given. When the
 * pull-out animation finishes, capDone is set and the gate falls back to gate.show (the moment the annotation appears).
 *
 * Why we can't wait for captions forever: the pull-out plays only once per clip; when scrolling back up and down again the captions are already in place,
 * no animation will fire again, and if the gate were still waiting for it it would never open — reading as "it plays the first time you scroll down,
 * but not after you scroll back" (measured 2026-07-24).
 *
 * For clips without a caption track / on mobile / under reduced-motion, capIdx is null and show is always used.
 * Pure function; unit tests in tests/unit/svs-play-gate.test.mjs.
 */
function pickGateStart(gate, capDone) {
	if (!gate) {
		return Infinity;
	}
	if (gate.capIdx == null) {
		return gate.show;
	}
	// Captions still waiting to pull out (or pulling out) → gate shut tight. The pull-out is time-driven; when it finishes capDone
	// is set and only then does the gate fall back to show; by then rawProgress is already past popEnd >= show, so it passes immediately.
	return capDone && capDone[gate.capIdx] ? gate.show : Infinity;
}

/**
 * Upper bound on product height such that "the whole unit fits horizontally".
 *
 * Layout unit (since 2026-07-25) = left product's left edge → **caption column's right edge**, horizontally centered as a whole: the phone and
 * captions are one piece, and that piece is centered together with the left product (previously only the "left product → phone" pair was
 * centered, with the captions hanging in the blank space to the right of the phone, so the whole block looked shifted right).
 *
 *   unitW(H) = (pairK + stageGapK)·H + capW
 *
 * The strip has inset·H of empty frame on its left; centering pushes it to unitLeft − inset·H, and this must not go off screen:
 *
 *   (vw − unitW)/2 − inset·H >= edge
 *   → H <= (vw − 2·edge − capW) / (pairK + stageGapK + 2·inset)
 *
 * This one bound also subsumes the two old bounds: the old hMax (strip doesn't go off screen, but the unit didn't include the caption column) and the old
 * captionWidthCeiling (the caption column lived in the right-hand blank space and wrapped below the phone when it didn't fit). The caption column is now
 * part of the unit with its width welded by CSS (--svs-cap-w); if it doesn't fit, the product shrinks instead — it no longer wraps.
 * Right-side blank space = unitLeft = edge + inset·H > edge, so the right side needs no separate constraint.
 *
 * pairK = left strip width − product inset + phone width + gap between the two products; stageGapK = column gap between phone ↔ captions;
 * both are expressed "per unit of product height". capW / edge are pixels.
 * Pure function; unit tests in tests/unit/svs-caption-width-budget.test.mjs.
 */
/**
 * Upper bound on product height such that "the whole unit doesn't exceed the page grid".
 *
 * band = min(header content band, the theme's --grid-width). Whole unit (left product's left edge → caption text's right edge)
 * width = unitK·H + capW, plus the bit pushed out after unitShift moves it right:
 *
 *   unitK·H + capW + |shift|·H <= band
 *   → H <= (band − capW) / (unitK + |shift|)
 *
 * 2026-07-25: changed from "canvas (strip + viewfinder) ≤ header band" to this bound: ① the caption column must be counted,
 * otherwise "the whole unit doesn't exceed the grid" is meaningless; ② band must take the min with --grid-width — this store's header has
 * header--full-width enabled, and the header band measures 2191px at 2560; using it as the bound is no bound at all.
 *
 * When band is null (toggle off / can't be measured) there is no limit. When capW has already eaten the whole band, return 0, which is caught by the caller's
 * MIN_BAND_PRODUCT gate (such a viewport can't hold this layout anyway).
 * Pure function; unit tests in tests/unit/svs-header-band.test.mjs.
 */
function bandHeightCeiling(band, capW, unitK, shiftAbs) {
	if (!band || !(band > 0)) {
		return Infinity;
	}
	const denom = unitK + Math.abs(shiftAbs || 0);
	if (!(denom > 0)) {
		return Infinity;
	}
	return Math.max(0, (band - capW) / denom);
}

function unitWidthCeiling(vw, pairK, stageGapK, capW, inset, edge) {
	const denom = pairK + stageGapK + 2 * inset;
	if (!(denom > 0)) {
		return Infinity;
	}
	// When the viewport is too narrow to even hold the caption column, return 0 and let the caller's floor (Math.max(120, …)) catch it;
	// don't return a negative — a negative ceiling would make Math.min drag the whole solution negative.
	return Math.max(0, (vw - 2 * edge - capW) / denom);
}

// The **single** smoothing source (in the progress domain) shared by frame and offset. The seeker only does frame-grid quantization and no longer adds a second layer of lag
// — two filters each lagging on their own are bound to misalign, which is exactly where "the product gets flat-cut by the viewfinder" came from.
const SEEKER_QUANTIZE_ONLY_MS = 1;
// Fallback deadline for pre-hidden captions: if the engine still hasn't set up the pull-out window by then, give the captions back to the user (see armCaptions).
const CAP_ARM_FALLBACK_MS = 4000;
// Within this progress difference, snap to equal: after scrolling stops the offset must land **exactly** on the segment value (flat segment = parkOffset
// exactly); otherwise a floating-point tail leaves the strip forever slightly off and the product's bottom gets cut by the viewfinder.
const PROGRESS_SNAP_EPSILON = 1e-5;

/**
 * First-order low-pass of scroll progress — the single smoothing point in the whole module.
 *
 * Why smoothing lives in the **progress domain** rather than each output domain: the frame (mapPoints) and the flush offset
 * (offsetPoints) share the same set of p breakpoints, so as long as both sample the same smoothed progress they are **synchronized by
 * construction**; there is no need to tune a separate time constant for the offset, and "offset in place but frame still stale" pushing the product
 * out of the viewfinder (the failure mode recorded on 2026-07-22) can't happen.
 *
 * The old design low-passed the offset separately and hard-snapped when "the target equals the previous frame", which caused two symptoms: during slow scrolling
 * scrollY is rounded by the browser and repeats frame to frame → snapping even during motion segments → jumps of 20~40px (judder); and whenever there was
 * lag, parkOffset never arrived → the strip sat too low → the product's bottom got flat-cut. See the investigation history in
 * tests/unit/svs-offset-sync.test.mjs.
 *
 * @param {number} raw Current raw scroll progress
 * @param {number|null} smoothed Previous frame's smoothed progress; null = first frame, use raw directly
 * @param {number} dtMs Frame interval
 * @param {number} smoothMs Time constant (merchant-configurable smooth_ms)
 * @returns {number} This frame's smoothed progress
 */
function advanceProgress(raw, smoothed, dtMs, smoothMs) {
	if (smoothed == null) {
		return raw;
	}
	const tau = smoothMs > 0 ? smoothMs : 1;
	const next = smoothed + (raw - smoothed) * (1 - Math.exp(-dtMs / tau));
	return Math.abs(raw - next) <= PROGRESS_SNAP_EPSILON ? raw : next;
}

/* ---------- Viewfinder geometry clamp (2026-07-26) ----------
 *
 * Symptom: when handing off to the next module, for a moment the product's bottom gets **flat-cut** (rounded corners disappear).
 *
 * Real-device pixel measurement (drive2/diag/calib in tmp/harness, reading video frame pixels directly, no model):
 *   · Deepest overrun 7~8px, at rawP≈0.80 / ct≈10.7s, i.e. mid-travel in the last segment
 *   · There |ty − mapped target(smoothProgress)| = 0.00 → the offset follows faithfully, **it is not lag**
 *   · |ty − mapped target(rawProgress)| = 3.17px → even with zero lag it would still cut about 4.8px
 *   · Calibration: the product's bottom edge as a fraction of frame height rises with ct, capping at 98.38% from ct≈10.88s onward,
 *     while the offset keeps ramping linearly to parkOffset — in that window "the product is already at its lowest but the strip hasn't risen enough"
 *
 * So this is **the mapping geometry leaving no margin for the product mid-segment**, not a smoothing problem. The offset mapping only guarantees correctness at breakpoints
 * (dock points / park point); linear interpolation between them isn't in sync with the product's actual movement in the footage.
 *
 * Fix: add a hard geometric floor — the strip may not sit lower than the position where "the product's bottom edge just stays inside the viewfinder".
 * It only takes effect on the few mid-segment frames that would cut; dock points and parkOffset never trigger it (measured values in the unit tests). */

// The product's bottom edge as a fraction of frame height: the manifest only gives the center, so add half the height to get the bottom edge, capped at the measured maximum
// (once the product settles in the frame it doesn't move down further; extrapolating past the cap has no basis). Both constants were
// calibrated by scanning the whole clip with tmp/harness/calib.mjs: median height 12.27% → half height 6.14%, max bottom edge 98.38%.
const PRODUCT_HALF_H_FRAC = 0.062;
const PRODUCT_BOTTOM_MAX_FRAC = 0.985;
// The product's height as a fraction of the frame (same calib run: median height 12.27%). section-handoff uses it
// to scale the ghost image to **exactly the same size** as the product in the footage — when the hand-tuned handoff_src_width is off, the size differs by
// 15.5% (measured ghost 436px vs footage product 378px), so aligning one edge misaligns the other by tens of px (ghosting).
const PRODUCT_HEIGHT_FRAC = 0.124;

/**
 * Position of the product's bottom edge in the frame (as a fraction of frame height).
 * @param {{manifest:Array<{t:number,y:number}>|null, duration:number, ct:number}} o
 * @returns {number|null} Returns null when data is insufficient (clamp off, no guessing)
 */
function productBottomFrac(o) {
	const m = o && o.manifest;
	if (!Array.isArray(m) || m.length < 2 || !(o.duration > 0)) {
		return null;
	}
	const t = o.ct / o.duration;
	const slope = (a, b) => (b.y - a.y) / (b.t - a.t);
	let center;
	if (t <= m[0].t) {
		center = m[0].y - (m[0].t - t) * slope(m[0], m[1]);
	} else {
		center = null;
		for (let i = 0; i < m.length - 1; i++) {
			if (t <= m[i + 1].t) {
				center = m[i].y + (t - m[i].t) * slope(m[i], m[i + 1]);
				break;
			}
		}
		if (center == null) {
			const L = m.length - 1;
			center = m[L].y + (t - m[L].t) * slope(m[L - 1], m[L]);
		}
	}
	return Math.min(PRODUCT_BOTTOM_MAX_FRAC, center + PRODUCT_HALF_H_FRAC);
}

/**
 * Entry hang offset: makes the product hang from the bottom edge of the previous module at the moment of reveal.
 *
 * On 2026-07-25 this offset was welded to 0, on the grounds that "the left column became a clipping viewfinder, so the strip can't draw outside it". But the left column's
 * clip-path is inset(-800px -320px), with 800px of slack above and below — it can draw outside. With it welded, the product could only
 * appear inside the section, reading as "popping out from under the heading" rather than falling from the previous module (2026-07-26 feedback).
 *
 * liftFrac = **fraction of the product sticking out above the top of the viewfinder**: 0 = top edge exactly at the top with nothing showing, 0.5 = half the product showing,
 * 1 = the whole product above the viewfinder (i.e. invisible). A negative return value = strip moves up.
 *
 * The top-edge position is obtained by extrapolating the manifest to t=0 (the manifest gives the product-center fraction at each dock point), so when the footage changes
 * you just re-calibrate the manifest and this follows automatically, with no pixel value to hand-tune.
 *
 * @param {{manifest:Array|null, duration:number, frameHeight:number, liftFrac:number}} o
 * @returns {number} translateY (px) to apply during the entry hold segment; 0 when data is insufficient (falls back to the old behavior)
 */
function entryLiftOffset(o) {
	const bottom = productBottomFrac({ manifest: o.manifest, duration: o.duration, ct: 0 });
	if (bottom == null || !(o.frameHeight > 0)) {
		return 0;
	}
	const topFrac = bottom - PRODUCT_HEIGHT_FRAC;
	return o.frameHeight * (topFrac - o.liftFrac * PRODUCT_HEIGHT_FRAC);
}

/**
 * Clamp the offset so "the product's bottom edge doesn't cross the viewfinder's bottom edge". Only ever **raises** the strip, never pushes it down.
 * @param {{ty:number, bottomFrac:number|null, boxHeight:number, frameHeight:number}} o
 * @returns {number} The clamped translateY
 */
function clampOffsetToViewfinder(o) {
	if (o.bottomFrac == null || !(o.boxHeight > 0) || !(o.frameHeight > 0)) {
		return o.ty;
	}
	// The frame top is aligned with the viewfinder top, so the product's on-screen bottom edge = ty + bottomFrac × frameHeight;
	// it must be ≤ boxHeight.
	return Math.min(o.ty, o.boxHeight - o.bottomFrac * o.frameHeight);
}

/**
 * The strip's opacity for this frame.
 *
 * opacityPoints is **one curve governing two things**, and the two segments have entirely different gates:
 *   · Entry reveal (v=0 before pAppear → 0.3 → fully opaque at the first dock point): the strip is supposed to be hidden anyway,
 *     which has nothing to do with whether anyone takes over, so it follows the curve **unconditionally**.
 *   · Exit handoff (fade out after pDone to section-handoff's ghost image): after fading out someone must take over,
 *     otherwise the product vanishes into thin air (much worse than ghosting), so it waits for handoffArmed.
 *
 * 2026-07-26 regression: handoffArmed was originally applied to the whole curve, and the else branch forced the strip back to
 * opaque. So as soon as the page loaded (flight not yet built) the strip was forced visible — the entry reveal broke and the product showed
 * directly at the top of the section; once section-handoff wrote handoffArmed the curve took over, and with progress still before pAppear
 * the curve value was 0, so the product suddenly vanished. The user saw "shows directly at first, then disappears by itself a moment later", and the timing of the
 * disappearance drifted with when the handoff got ready. Regression test in tests/unit/svs-entry-reveal-gate.test.mjs.
 *
 * @param {{curve:number, p:number, park:number|null, armed:boolean}} o
 * @returns {number} Opacity to apply for this frame
 */
function resolveStripOpacity(o) {
	// Not yet at the park point (or this curve has no exit tail) → entry segment, follow the curve unconditionally
	if (o.park == null || o.p < o.park) {
		return o.curve;
	}
	return o.armed ? o.curve : 1;
}

/**
 * Dock band hit test. bands = [{start, end}] (in progress units); returns -1 on a miss.
 *
 * A dock band is the segment where "both the frame and offset mappings are flat", i.e. the [pStart, holdEnd] bounded by the two
 * equal-valued breakpoints that measureDocks pushes into both points / offsets, plus the final park
 * [parkProgress, 1]. Only in these segments is the frame still, so the static image and the video's current frame are pixel-equivalent and
 * a hard cut can't be seen.
 *
 * ⚠️ Don't use showStart/showEnd as dock bands — those are the **annotation window** (dock window ∩ right video
 * fully visible, also with an annoMinVh floor), which is narrower than and offset from the frame-freeze band; using it would cut images while the product
 * is still moving. The different names are no coincidence; the distinction is deliberate.
 */
function holdBandIndex(progress, bands) {
	if (!bands || !bands.length) {
		return -1;
	}
	for (let i = 0; i < bands.length; i += 1) {
		const band = bands[i];
		if (progress >= band.start && progress <= band.end) {
			return i;
		}
	}
	return -1;
}

/**
 * Which dock static image to show this frame. Returns -1 on a miss.
 *
 * Inside a band → that band's image (consistent with holdBandIndex).
 * Outside a band → only when **there's no video at all** fall back to the previous dock point's image; before the first dock point
 * fall back to the first one. When a video is available, don't fall back — motion segments should be drawn by the video, and stuffing in a still image is a step backward.
 *
 * Why fall back (2026-07-27, decided after the user saw the actual result): the right column's poster is **always present**
 * (as long as the video element exists, the poster is always painted), while the left column originally only showed images inside dock bands. So
 * during the product's "falling" segment there was something on the right and nothing at all on the left; user feedback: "at least the static images on both sides should appear together".
 *
 * ⚠️ Cost: dock images are **whole-canvas** images with the product's position in the frame welded in, while translateY is still
 * interpolating — mid-fall the product "slides" rather than "falls". This is a degraded form, handed back to video as soon as the low-res one arrives.
 * **Never touch translateY for this** (the 2026-07-25 approach introduced regressions of "two products appearing at once + the product's bottom
 * getting clipped", all of which were reverted).
 *
 * @param {number} progress Current progress
 * @param {Array<{start:number,end:number}>|null} bands Dock bands
 * @param {boolean} noVideo Whether there's no video at all (videoTier === "none")
 * @returns {number}
 */
function holdStillIndex(progress, bands, noVideo) {
	const hit = holdBandIndex(progress, bands);
	if (hit >= 0 || !noVideo || !bands || !bands.length) {
		return hit;
	}
	let fallback = 0;
	for (let i = 0; i < bands.length; i += 1) {
		if (bands[i].end < progress) {
			fallback = i;
		}
	}
	return fallback;
}

/**
 * Timing decision for the hot swap from low-res → high-res.
 *
 * The swap resets src and waits for loadedmetadata; for tens to hundreds of ms the frame is uncontrolled. Only execute it
 * "inside a dock band (frame still + static image covering)" or "when the section is off-screen (nobody can see)".
 * Timeout fallback: in some layouts it may be neither in a dock band nor off-screen for a long time; we can't stay stuck
 * on low-res forever because of that — better to flash once.
 */
function shouldSwapNow(state) {
	if (!state.sectionActive) {
		return true;
	}
	if (state.inHold) {
		return true;
	}
	return state.waitedMs >= state.maxWaitMs;
}

/**
 * Whether the caption track may pull out this frame.
 *
 * The three stages are "annotation → phone pops out of the product → captions pull out from the phone's right edge → video plays only after captions finish".
 * The third stage's trigger **used to look only at scroll progress** (popEnd), so before the right video had even appeared (the left strip hadn't even
 * finished loading) the captions were already laid out in full — the third stage ran ahead of the second (2026-07-26 user feedback).
 *
 * Invariant set by the user: captions follow the right video; captions may not appear before the video does. So there is an extra
 * "has a frame to paint" gate here. It **cannot** be changed to wait for the video to play: the loop's play gate (pickGateStart) is already
 * waiting for the captions to finish pulling out (capDone), and waiting on each other would deadlock. Having a frame ≠ playing — that's exactly the moment the phone has landed but hasn't started moving
 * yet, which is precisely when the captions should pull out.
 *
 * Pure function; regression test in tests/unit/svs-caption-follows-video.test.mjs.
 *
 * @param {{p:number, at:number, fired:boolean, ready:boolean}} o
 * @returns {boolean}
 */
function capCanReveal(o) {
	if (o.fired || o.p < o.at) {
		return false;
	}
	// When ready is unknown (no video available), treat it as not ready: better to appear late than to run ahead of the video again
	return o.ready === true;
}

/**
 * Caption tracks that were pre-hidden but for which the engine didn't build a pull-out window — these must be restored, or they stay invisible forever.
 *
 * measureDocks' forEach skips individual items (zero-length window, manifest time going backward), so that track gets no
 * window. If the fallback only checked "no windows built at all", such a stray track would never show — captions are body content,
 * far more serious than an animation not playing.
 *
 * @param {Array<Element>|null} armed Tracks that were pre-hidden
 * @param {Array<{el:Element}>|null} windows Pull-out windows that were built
 * @returns {Array<Element>} Tracks that need restoring
 */
function orphanCaptions(armed, windows) {
	// A window not bound to a video = nothing to follow = never gets released, so it also counts as an orphan
	const covered = new Set((windows || []).filter((w) => w.video).map((w) => w.el));
	return (armed || []).filter((el) => !covered.has(el));
}

/**
 * Width of the header content band: the segment **between** the logo and the right-side icons.
 *
 * 2026-07-24 feedback: the alignment target isn't the whole `.header--inner` (logo left edge ↔ icons right edge),
 * but "the header width **minus the logo and minus the right-side icons**" — the band where the menu sits.
 * Gap between the two boxes = max(both left edges) − min(both right edges); this formula also holds for RTL
 * (the formula is unchanged when the logo and icons swap places).
 *
 * Pure function; unit tests in tests/unit/svs-header-band.test.mjs.
 * @param {{left:number,right:number}} logo The logo's rect
 * @param {{left:number,right:number}} icons Rect of the right-side account/cart icon area
 * @returns {number} Band width in px; returns 0 when the two overlap or can't be measured, and the caller falls back
 */
function headerInnerBand(logo, icons) {
	if (!logo || !icons) {
		return 0;
	}
	const band = Math.max(logo.left, icons.left) - Math.min(logo.right, icons.right);
	return band > 0 ? band : 0;
}

// typeof guard: this file must be importable directly by node for unit tests, and Node has no customElements.
// In browsers customElements always exists, so behavior is unchanged.
if (typeof customElements !== "undefined" && !customElements.get("scroll-video-split")) {
	// Small settle after the gate opens before the paired clip starts playing.
	// 2026-07-23: was 1000ms ("give the callout a moment first"), but the ask
	// is the opposite — the clip should light up WHEN the annotation appears,
	// not a beat later. Kept non-zero only to swallow a fast scroll-through
	// (gate opens then immediately closes) so it doesn't flash a frame of play.
	const GATE_PLAY_DELAY_MS = 160;

	class ScrollVideoSplit extends HTMLElement {
		connectedCallback() {
			if (!window.ScrollVideoCore) {
				return;
			}
			this.core = window.ScrollVideoCore;
			// Report in to the synchronous inline script in the section: it adds the caption tracks' pre-hide marker (data-svs-cap-arm),
			// and if the engine is slow to report in it removes the marker itself and gives the captions back. Placed **after** the core check —
			// it only counts once the engine has really started; when the return above hits, we must not report in.
			this.dataset.svsEngine = "1";
			// Temporary debug readout: append ?svs-debug to the URL to overlay the
			// left video's current time (= the ffmpeg -ss timestamp of the frame
			// on screen) and scroll progress. Lets a tester read off the exact
			// frame that still shows an issue. Harmless when the param is absent.
			if (new URLSearchParams(location.search).has("svs-debug")) {
				const dbg = document.createElement("div");
				dbg.style.cssText =
					"position:fixed;top:8px;left:8px;z-index:99999;background:rgba(0,0,0,.8);color:#0f0;" +
					"font:700 16px/1.4 monospace;padding:6px 10px;border-radius:6px;pointer-events:none;white-space:pre;";
				document.body.appendChild(dbg);
				this.debugReadout = dbg;
				// Outline every right-column block. "This screen is empty" is
				// ambiguous between a layout gap and a video that occupies the
				// space but paints nothing — an outline settles it in one
				// screenshot, and the label carries each clip's load/play state
				// so a blank-but-present clip names its own reason.
				const style = document.createElement("style");
				style.textContent =
					".scroll-video-split__item{outline:3px solid rgba(255,0,0,.8);outline-offset:-3px;position:relative}" +
					".scroll-video-split__text{outline:3px solid rgba(0,90,255,.8);outline-offset:-3px}" +
					".scroll-video-split__item::after{content:attr(data-svs-state);position:absolute;" +
					"background:rgba(255,0,0,.85);color:#fff;font:700 13px/1.3 monospace;padding:3px 6px}";
				document.head.appendChild(style);
				this.debugOutline = true;
			}
			this.scrubVideo = this.querySelector(".scroll-video-split__scrub");
			this.loopVideos = Array.from(this.querySelectorAll(".scroll-video-split__loop"));
			this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
			// Mobile (<1100px) drops the entire scrub/dock/handoff mechanism: the
			// left strip is hidden (CSS) and never set up, and each right block
			// instead shows a plain phone-clip + feature-clip loop pair. Read once
			// for the one-time setup decisions (setupScrub / measureDocks); the
			// per-frame loop gate reads matchMedia live instead, so a viewport
			// toggle in the theme editor still ungates the clips without a teardown.
			//
			// ⚠️ The 1100 breakpoint is written separately in 5 places; change one and you must change them all (CSS media queries can't reference
			//    variables, so only this comment ties them together):
			//      assets/scroll-video-split.js          this line
			//      assets/scroll-video-split.css         3 min-width + 1 max-width block
			//      assets/scroll-video-split-captions.css same as above
			//      assets/scroll-video-split-captions.js  mobileMq
			//      sections/scroll-video-split.liquid     <source media="..."> (static product image;
			//                                             if not changed, the whole image block won't load on tablet)
			// It used to be 768 (the theme's main breakpoint). Raised to 1100 on 2026-07-24: at tablet widths the "left strip + phone +
			// captions" trio laid out horizontally was awkward however it was tuned — the product got squeezed to 160~330px, the caption text was bigger than the phone,
			// and the one-screen centered composition left large blank areas above and below. Tablets now use the mobile vertical layout.
			this.mqDesktop = window.matchMedia("(min-width: 1100px)");
			this.isDesktop = this.mqDesktop.matches;
			this.threshold = Math.min(1, Math.max(0.1, (parseFloat(this.dataset.threshold) || 80) / 100));
			// Dock band height as a fraction of the viewport (data-dock-window,
			// percent). Independent from the play threshold on purpose.
			// Defaults tuned by hand on the demo (2026-07-06): 40vh window +
			// 100ms smoothing read as "brisk but settled".
			this.dockWindow = Math.min(1.5, Math.max(0.2, (parseFloat(this.dataset.dockWindow) || 40) / 100));
			// Ceiling on that window, solved per viewport in layout(): the authored
			// value is free scroll, and anything past what the layout can cover
			// scrolls a neighbouring block into view (see layout()'s joint solve).
			// Starts at the authored value so the passes layout() runs before the
			// solve behave exactly as before.
			this.dockTravel = this.dockWindow;
			// Cap how deep any dock (and the trailing hold) reaches into the
			// scrub travel, as a fraction. Some assets run the product fully out
			// of frame in the last stretch; without a cap the final dock lands on
			// a frame where the product's bottom is already clipped. The paired
			// annotation anchor uses the same cap, so the callout never drifts off
			// the product. Default 100% = no cap (exact prior behavior).
			this.dockMax = Math.min(1, Math.max(0.5, (parseFloat(this.dataset.dockMax) || 100) / 100));
			// Pull-out: the right item slides out of the middle of the left
			// product and resolves from blurred to sharp, finishing exactly when
			// the callout appears and the clip starts playing. Scroll-driven, and
			// RATCHETED — progress only ever increases, so scrolling back up does
			// not play it in reverse and each item performs it once (chosen
			// 2026-07-22 over a reversing version and a time-driven one; a
			// time-driven pop desyncs from the scroll-driven fall it is supposed
			// to be caused by).
			this.popOut = this.dataset.popOut !== "false";
			// Scroll distance the pull-out consumes, px. It lives in the approach
			// to a dock (~680px between docks), never in the hold — the hold is
			// only 53px on a laptop after the one-screen solve.
			this.popRun = Math.min(700, Math.max(80, parseFloat(this.dataset.popRun) || 190));
			this.popBlur = Math.min(40, Math.max(0, parseFloat(this.dataset.popBlur) || 16));
			// Fraction of the run over which opacity reaches 1.
			this.popFade = Math.min(1, Math.max(0.2, (parseFloat(this.dataset.popFade) || 60) / 100));
			// Where the pull-out STARTS, relative to the callout appearing. 0 = the
			// instant it shows. The press is the cause and the panel is the effect,
			// so the panel must not finish arriving before the press is announced —
			// the first cut ENDED at showStart, i.e. effect before cause ("I want a video to pull out right at
			// the instant the annotation is about to appear", 2026-07-22). Negative starts early.
			this.popLead = Math.min(300, Math.max(-300, parseFloat(this.dataset.popLead) || 0));
			// Minimum time the callout stays up, as a FRACTION OF VIEWPORT HEIGHT
			// rather than a pixel hold: the dock window it would otherwise be tied
			// to is itself solved per viewport (53-233px across the range here), so
			// a fixed px hold reads completely differently on a laptop and on a
			// 1440p screen. Expressed this way the callout is guaranteed the same
			// share of a screenful everywhere ("when scrolling down to 15% of the visible screen,
			// the annotation is still there", 2026-07-22).
			//
			// Extra scroll the callout stays up for after its dock window closes.
			// A linger was tried and removed once before (the artwork travels on and
			// the pill points at nothing), but that was against a 320px dock window;
			// the one-screen solve has since cut it to ~84px, so the callout now
			// blinks ("it disappears too fast", 2026-07-22). Kept OUT of the pull-out window on
			// purpose — the panel still has to land while the product is parked.
			this.annoMinVh = Math.min(60, Math.max(0, parseFloat(this.dataset.annoMin) || 0)) / 100;
			// Minimum scroll the ENDING gets to travel from the last dock's levelling to
			// its park position. That move is a REVERSAL — every other transition in the
			// section pushes the strip down, this one pulls it back up (~306px here) so
			// the last frame's bottom can sit on the window bottom, which the ghost
			// hand-off takes off from (2026-07-20). The reversal cannot go away without
			// re-authoring the ending; spreading it over more scroll makes it read as a
			// slow settle rather than a bounce ("there are a lot of bounce-back moments", 2026-07-22).
			// Whatever it takes comes out of the hand-off's flight distance — a dial,
			// not a fix.
			this.parkRunway = Math.min(1200, Math.max(0, parseFloat(this.dataset.parkRunway) || 0));
			this.popSeen = [];
			this.popWindows = null;
			// Caption track "pull-out" — the second stage of the three-stage entry (2026-07-24 feedback: "pop the right-side
			// video out from the left; once the right video has popped out and settled, the captions next to it then pop out of the right video as a whole,
			// and only then does the right video start playing").
			//
			// Time-driven, not scroll-driven — the opposite of pull-out, and deliberately so. The pull-out's
			// "cause" is the product continuously falling (a continuous quantity), so it must follow scrolling; time-driven would fall out of sync.
			// The captions' "cause" is the **discrete event** "the video has settled"; following scrolling would actually make it not read as a single
			// action: during slow scrolling it creeps along bit by bit, you can't see a pull-out, let alone that playback follows
			// (measured 2026-07-24: "when scrolling slowly… you don't know it's playing").
			// It fires on settling and slides out over a fixed duration on its own; only once it finishes does the play gate open.
			this.capRevealMs = Math.min(2000, Math.max(150, parseFloat(this.dataset.capRevealMs) || 620));
			this.capDone = [];
			this.capWindows = null;
			this.capTimers = new Set();
			this.smoothOffset = null;
			// The single smoothed progress in the whole module (shared by frame and offset)
			this.smoothProgress = null;
			// Distance of the product's bottom edge from the frame bottom in the footage, as a fraction; used by section-handoff to calibrate the ghost image's takeoff point.
			// A hand-tuned handoff_src_offset percentage that's off makes the ghost image differ from the footage product by tens of px,
			// with two products visible at once during the cross-fade (user feedback: "ghosting"). Calibrated by tmp/harness/calib.mjs.
			this.footageProductBottomFrac = PRODUCT_BOTTOM_MAX_FRAC;
			this.footageProductHeightFrac = PRODUCT_HEIGHT_FRAC;
			this.presentedTime = null;
			this.viewfinderH = 0;
			this.stripFrameH = 0;
			this.smoothMs = Math.min(300, Math.max(40, parseFloat(this.dataset.smoothMs) || 100));
			this.active = false;
			this.scrubStarted = false;
			this.duration = 0;
			this.rafId = null;
			this.handles = [];
			this.mapPoints = null;
			// Authored-holds manifest (data-dock-manifest): "t%,y% | t%,y% | …",
			// entry N describing the frame held while right item N is docked —
			// t = hold-frame time as a fraction of the asset duration, y = the
			// product center inside the strip at that frame. Scene-cut footage
			// (still holds + fast transitions) breaks the constant-speed
			// assumption behind geometric dock inference, so the times must
			// come from the asset itself. null = geometric mode (prior
			// behavior, correct for constant-speed assets).
			this.dockManifest = this.parseManifest(this.dataset.dockManifest);
			this.offsetPoints = null;
			this.appliedOffset = 0;
			this.opacityPoints = null;
			this.appliedOpacity = 1;
			// Manifest mode: loop video → the raw progress at which it may
			// START playing (its dock's show window opening — the same moment
			// the paired annotation fades in). The visibility observer still
			// owns loading and stopping; this only delays the start so the
			// sequence always reads annotation-then-motion.
			this.playGates = null;
			// Pending gate-start timers (video → timeout id): once a gate opens the
			// clip waits GATE_PLAY_DELAY_MS before moving, so the annotation has a
			// beat to be read first. Scrolling back out of the window (or
			// unmounting) cancels the pending start.
			this.gateTimers = new Map();
			this.annotations = Array.from(this.querySelectorAll(".scroll-video-split__annotation")).map((el) => ({
				el: el,
				showAt: parseFloat(el.dataset.showAt) || 0,
				hideAt: parseFloat(el.dataset.hideAt) || 100,
				visible: false,
				// The block's authored leader length, captured before any
				// measurement can overwrite it. The collision guard in
				// measureDocks shortens the line on cramped viewports by
				// writing the SAME custom property Liquid set inline, so the
				// authored value has to live somewhere else or the first clamp
				// would destroy it for good.
				line: parseFloat(el.style.getPropertyValue("--svs-anno-line")) || 72,
			}));
			if (this.reducedMotion) {
				this.annotations.forEach((annotation) => annotation.el.classList.add("is-visible"));
			}
			// Right clip's subject bounds as fractions of its OWN frame
			// ("left%,right%"). The clip bakes marketing copy into the right
			// half of the frame, so the phone sits well left of the frame's
			// centre — a fact no DOM measurement can recover. Everything else
			// centerPair needs (column boxes, cover crop, viewport) it measures
			// itself. Blank/malformed = feature off, box centering as before.
			this.rightSubject = this.parseSubject(this.dataset.rightSubject);
			// --- Desktop size model constants (all measured off the footage) ---
			// The pair used to be sized by two unrelated variables (left =
			// column width, right = viewport height), so its proportions
			// changed at every resolution. Now ONE number — the product's
			// on-screen height — drives both, and these describe how each
			// subject sits inside its own frame so the boxes can be reverse-
			// engineered from it.
			// Vertical bounds of the phone inside the right clip, the missing
			// twin of rightSubject (which is horizontal). Needed because the
			// clip's frame is ~15% taller than the phone.
			this.rightSubjectV = this.parseSubject(this.dataset.rightSubjectV) || { a: 0.052, b: 0.918 };
			// Horizontal bounds of the product inside the left strip. Only `a`
			// is load-bearing (it says how much empty strip hangs left of the
			// product, which the pair centering has to discount).
			// Must stay in sync with the setting's default — this fallback is
			// what an empty field falls back to, and a mismatch would move the
			// pair sideways the moment a merchant clears the box.
			this.leftSubject = this.parseSubject(this.dataset.leftSubject) || { a: 0.219, b: 0.804 };
			// Left product height ÷ strip frame width. The strip is 1:9 and the
			// product occupies a fixed slice of it, so this single ratio turns a
			// wanted product height into the strip width that produces it.
			//
			// ONE global ratio, deliberately: the strip is a single video that
			// scrubs continuously, so its width is solved once — there is no
			// per-dock k to be had. So whenever the three hold frames aren't
			// equally tall, some dock renders off-size and no setting can fix it
			// — the footage itself has to be equalised (2026-07-25: the shipped
			// asset now holds 1.283/1.286/1.288, a 0.4% spread; see the asset
			// spec doc for the per-frame rescale that produced it).
			this.leftK = Math.min(4, Math.max(0.2, parseFloat(this.dataset.leftK) || 1.286));
			// Right/left size trim: the left product is a tilted slab and the
			// right one a portrait phone, so geometric equality isn't always
			// visual equality. 1 = both exactly --svs-ph tall.
			this.productScale = Math.min(1.6, Math.max(0.6, (parseFloat(this.dataset.productScale) || 100) / 100));
			// Extra breathing space BETWEEN the two products, as a fraction of
			// the product height (so it scales with everything else). The
			// footage already leaves ~21% of the strip's width empty to the
			// right of the product; this adds to that. Implemented by sliding
			// the right frame inside its viewfinder — see the CSS.
			// The fallback must equal the schema default (12, tightened from 38 in two rounds on 2026-07-25 — the feedback was
			// "the left product should be a bit closer to the right" → "a bit further still"): an empty field must not quietly fall back to a layout different from what the schema claims.
			const pairGapRaw = parseFloat(this.dataset.pairGap);
			this.pairGap = Math.min(0.8, Math.max(0, (Number.isFinite(pairGapRaw) ? pairGapRaw : 12) / 100));
			// Horizontal nudge of the whole unit (left product → caption text's right edge) beyond centering, as a fraction of product height,
			// positive moves right. Why it exists: the three caption bodies differ in length, so the frame that expands a short sentence leaves a gap on the right,
			// and --svs-left-offset has only one value for the whole section and can't compensate per frame — strict centering looks best on the long-sentence frame,
			// and the short-sentence frame looks shifted right. This knob hands "push it a bit by feel" to the theme editor instead of hard-coding a constant.
			// Default 0: horizontal placement is given structurally by "right edge aligned with the body block's right edge" (see
			// alignedOffset in layout()); this knob is just a manual nudge on top of that. On 2026-07-25 it defaulted to +8% for a while;
			// that was manually approximating the alignment line, and it went back to 0 once alignment landed.
			// Must equal the schema default (the fallback is what's used when the live template lacks this key).
			const unitShiftRaw = parseFloat(this.dataset.unitShift);
			this.unitShift = Math.min(0.15, Math.max(-0.15, (Number.isFinite(unitShiftRaw) ? unitShiftRaw : 0) / 100));
			// Breathing room above and below the one-screen group.
			this.breathing = Math.min(200, Math.max(0, parseFloat(this.dataset.breathing) || 24));
			// Optional ceiling on the product height (0 = none). On very tall
			// displays the height budget alone can grow the product past the
			// point where the right clip's baked copy survives the crop.
			this.productMax = Math.max(0, parseFloat(this.dataset.productMax) || 0);
			// Cap the artwork so the canvas (left strip + right viewfinder) is no
			// wider than the header's content band — the same band the copy above
			// is already sized to, so the module lines up with the page's grid
			// instead of running to the viewport edge.
			this.alignHeaderWidth = this.dataset.alignHeaderWidth !== "false";
			// Final trim on the solved height. The solver fills the screen by
			// construction (that IS the brief), which on a big display means a
			// very large product; this scales the answer down without touching
			// any of the relationships — the pair stays equal-sized, centred and
			// on one screen, just smaller with more air around it.
			const sizeRaw = parseFloat(this.dataset.productSize);
			// Fallback matches the setting's default (90), not 100 — an empty
			// field must not render larger than the schema advertises.
			this.productSize = Math.min(1, Math.max(0.5, (Number.isFinite(sizeRaw) ? sizeRaw : 90) / 100));
			// Solved by layout(); measureDocks adds it to the authored offset so
			// the docked group lands vertically centred without hand-tuning.
			this.dockAlignAuto = 0;
			this.productHeight = 0;
			// RETIRED (2026-07-21). Used to anchor the falling product on a
			// selector inside the PREVIOUS section (the product gallery card),
			// which pinned the pair wherever that neighbour happened to sit —
			// the pair read visibly off-centre and moved whenever the page above
			// changed. layout() now centres the pair on the viewport itself and
			// nothing reads this. The setting survives only so old configs load.
			// Where a docked clip comes to rest, as a fraction of the viewport
			// measured from its centre (positive = lower). Docking centres the
			// CLIP, but the copy block sits above it and the theme's sticky
			// header eats the top ~74px — at 0 the copy is pushed off the top
			// and what survives lands under the header. Nudging the rest point
			// down lets the copy + clip pair sit fully in the visible band.
			this.dockAlignOffset = Math.min(0.3, Math.max(-0.3, (parseFloat(this.dataset.dockAlignOffset) || 0) / 100));
			// Entry hang: fraction of the product sticking out above the viewfinder top (see entryLiftOffset). The fallback equals the schema
			// default — if they differed, pages where "this setting was never saved in the theme editor" would get a different layout.
			const liftRaw = parseFloat(this.dataset.entryLift);
			this.entryLift = Math.min(1, Math.max(0, (Number.isFinite(liftRaw) ? liftRaw : 45) / 100));
			this.grid = this.querySelector(".scroll-video-split__grid");
			this.measureDocks = this.measureDocks.bind(this);
			this.layout = this.layout.bind(this);
			this.tick = this.tick.bind(this);

			if (this.scrubVideo && this.isDesktop) {
				this.setupScrub();
			}
			/* Re-run when the width gate crosses into the desktop tier (2026-07-27).
			 *
			 * this.isDesktop is a **snapshot** taken in connectedCallback above, and setupScrub's gate
			 * is exactly that; everywhere else in the engine (gatedLoop / captions / tick / featureAspect) reads
			 * this.mqDesktop.matches live. So if the page loads below 1100 and the window is then widened, the left-column strip
			 * **never initializes** — no seeker, no dock bands, no dock images, the left side is completely empty; while the right column
			 * isn't governed by this gate and displays as usual. User measurement: width 1403, desktop layout true, but videoTier was
			 * undefined. onResize only re-runs layout(), which can't rescue it.
			 *
			 * this.seeker is used as the idempotency sentinel: setupScrub builds the seeker, attaches two observers and installs the touch
			 * unlock; running it again would register them twice. */
			this.onDesktopChange = (e) => {
				if (!e.matches) {
					return;
				}
				this.isDesktop = true;
				if (this.scrubVideo && !this.seeker) {
					this.setupScrub();
					this.layout();
				}
			};
			this.mqDesktop.addEventListener("change", this.onDesktopChange);
			if (this.loopVideos.length) {
				this.setupLoops();
			}
			// Caption tracks are hidden by default. This must happen here, not wait for measureDocks — see armCaptions.
			this.armCaptions();

			// Dock mapping depends on layout; measure after first layout and keep it
			// fresh across resizes and late metadata (the video's intrinsic aspect
			// affects the contain-fitted box).
			this.onResize = () => {
				clearTimeout(this.measureTimer);
				this.measureTimer = setTimeout(this.layout, 150);
			};
			window.addEventListener("resize", this.onResize);
			// layout() solves the size model and calls measureDocks itself (the
			// two are mutually dependent: dock geometry is measured off boxes
			// whose size layout is still solving for, so it iterates).
			requestAnimationFrame(this.layout);
			if (this.scrubVideo) {
				this.scrubVideo.addEventListener("loadedmetadata", this.layout);
			}
			// The right clip's real aspect decides how wide its frame has to be
			// to make the phone --svs-ph tall — re-solve once each clip knows.
			this.loopVideos.forEach((video) => {
				video.addEventListener("loadedmetadata", this.layout);
			});
			// The caption column's ink width is an input to centering (captionWidth()), and the ink width depends on the font:
			// measured while the first frame still uses the fallback font it differs from after the webfont lands, shifting the whole unit by a few
			// pixels until the next resize. Solve again once fonts are ready to make up that difference.
			if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === "function") {
				document.fonts.ready.then(this.layout);
			}
		}

		/**
		 * Caption track pre-hide — the starting point of the invariant "captions follow the right video".
		 *
		 * "Stuff them back behind the phone right after measuring" originally lived only in measureDocks, so the hiding timing followed layout
		 * measurement: in the window where measurement bailed early (strip has no metadata yet, box still 0) or arrived late, the captions were
		 * visible — the user's screenshot showed exactly that: page just loaded, thumbnails still lazy-loading, yet the caption track on the right was already fully
		 * laid out (2026-07-26). Hiding must be decoupled from any measurement: hide as soon as JS takes over.
		 *
		 * Only opacity is set, not transform: the phone's right edge hasn't been measured yet, dx is unknown, and writing a guessed value would make
		 * the real pull-out later slide in from the wrong place. measureDocks fills it in once it has measured dx.
		 */
		armCaptions() {
			// reducedMotion is not checked: that branch must also obey "no captions before the video appears"; it just skips
			// the pull-out animation on release (see triggerCaptionReveal). It used to be a complete bypass —
			// no pre-hide, no window, no trigger — so the captions were always visible and the invariant simply didn't hold on that branch.
			if (!this.mqDesktop.matches) {
				return;
			}
			this.armedCaptions = Array.from(this.querySelectorAll(".svs-steps")).filter((el) => !el.dataset.svsCapRevealed);
			if (!this.armedCaptions.length) {
				this.armedCaptions = null;
				return;
			}
			this.armedCaptions.forEach((el) => {
				el.style.transition = "none";
				el.style.opacity = "0";
			});
			// Fallback: if the engine ultimately fails to build a pull-out window (measurement keeps bailing early, video fails to load, script blocked),
			// give the content back to the user when time's up. Captions are body content and must never stay invisible forever just because an animation didn't run.
			this.capArmTimer = setTimeout(() => {
				this.capArmTimer = null;
				// Checked per track, not "restore only when no windows were built at all" — a single stray track would likewise stay invisible forever.
				this.releaseCaptions(orphanCaptions(this.armedCaptions, this.capWindows));
			}, CAP_ARM_FALLBACK_MS);
		}

		// Give the pre-hidden captions back to CSS (fallback path only; on the normal path triggerCaptionReveal's
		// pull-out animation clears the styles when it finishes).
		releaseCaptions(els) {
			const list = els || this.armedCaptions || [];
			list.forEach((el) => {
				el.style.transition = "";
				el.style.opacity = "";
				el.style.transform = "";
				// Clearing the inline style isn't enough: the CSS pre-hide ([data-svs-cap-arm] .svs-steps) still holds it down,
				// so it would still be opacity:0 afterward. This marker is the key to that CSS "released" rule.
				el.dataset.svsCapRevealed = "1";
			});
			if (!this.armedCaptions) {
				return;
			}
			this.armedCaptions = els ? this.armedCaptions.filter((el) => !els.includes(el)) : null;
		}

		// Drop a clip's pending gate-start (gate closed, or teardown).
		clearGateTimer(video) {
			const timer = this.gateTimers.get(video);
			if (timer) {
				clearTimeout(timer);
				this.gateTimers.delete(video);
			}
		}

		// The width getBoundingClientRect() coordinates live in — the layout
		// viewport, scrollbar EXCLUDED. Never window.innerWidth: where scrollbars
		// take space (Windows, Linux) innerWidth is ~15px wider than the box the
		// page is laid out in, and the pair centring below would push the strip
		// that far under the scrollbar. With this section deliberately unclipped
		// (see the note at the top of the CSS) that leaks out as a page-wide
		// horizontal scrollbar — invisible on macOS, whose overlay scrollbars make
		// the two numbers equal (2026-07-21).
		viewportWidth() {
			return document.documentElement.clientWidth || window.innerWidth;
		}

		disconnectedCallback() {
			this.deactivate();
			// Optional-chained: connectedCallback bails before this is set when
			// ScrollVideoCore is missing.
			this.gateTimers?.forEach((timer) => clearTimeout(timer));
			this.gateTimers?.clear();
			this.capTimers?.forEach((timer) => clearTimeout(timer));
			this.capTimers?.clear();
			if (this.capArmTimer) {
				clearTimeout(this.capArmTimer);
				this.capArmTimer = null;
			}
			["loadObserver", "runObserver", "lazyObserver", "playObserver"].forEach((key) => {
				if (this[key]) {
					this[key].disconnect();
				}
			});
			this.handles.forEach((handle) => handle.destroy());
			this.handles = [];
			window.removeEventListener("resize", this.onResize);
			if (this.onDesktopChange) {
				this.mqDesktop.removeEventListener("change", this.onDesktopChange);
			}
			clearTimeout(this.measureTimer);
			if (this.removeUnlock) {
				this.removeUnlock();
			}
		}

		// "22.2,32.1,31.3,25.9 | …" → [{t:0.222,y:0.321,gx:0.313,gy:0.259}, …].
		// Per entry: hold-frame time, product center y, and optionally the
		// glow-highlight point (x,y — strip coordinates) baked into the frame;
		// with a glow present, the dock-bound annotation anchors on it through
		// the same fit math (cover side-crop included), so no hand-tuned
		// x/offset settings are needed and none would survive viewport changes.
		// All-or-nothing: one malformed or out-of-order entry rejects the whole
		// manifest (geometric fallback) rather than silently mixing modes —
		// a half-applied manifest would dock some items on authored frames and
		// others on wrongly-inferred ones, which reads as a data-entry bug.
		parseManifest(raw) {
			if (!raw || !raw.trim()) {
				return null;
			}
			const entries = raw.split("|").map((part) => {
				const nums = part.split(",").map((token) => parseFloat(token));
				if (
					(nums.length !== 2 && nums.length !== 4) ||
					!nums.every((value) => isFinite(value) && value >= 0 && value <= 100)
				) {
					return null;
				}
				const entry = { t: nums[0] / 100, y: nums[1] / 100 };
				if (nums.length === 4) {
					entry.gx = nums[2] / 100;
					entry.gy = nums[3] / 100;
				}
				return entry;
			});
			if (!entries.length || entries.some((entry) => !entry)) {
				return null;
			}
			// Hold times must ascend: the scrub only ever plays forward.
			for (let i = 1; i < entries.length; i++) {
				if (entries[i].t <= entries[i - 1].t) {
					return null;
				}
			}
			return entries;
		}

		// The Liquid-side aspect is a PLACEHOLDER, good only until metadata
		// lands: it's hand-typed in the theme editor, so it drifts from the
		// file whenever a video is re-exported or swapped without updating the
		// field. Once the real dimensions are known they always win — the box
		// is sized from this ratio, and a wrong ratio means a box that doesn't
		// match its frame (letterbox under contain, a crop under cover).
		// Writing both forms keeps the CSS width clamp and the aspect-ratio
		// box in agreement; writing them only on a real change avoids a
		// pointless style invalidation when the typed value was already right.
		// The paired phone clip shares the item with the feature clip, so it has
		// to write its OWN property: both used to publish --svs-aspect and
		// whichever loaded last won, handing the feature clip the phone's 9:16
		// on mobile.
		ensureAspect(video, wrapper) {
			const mobile = video.classList.contains("scroll-video-split__mobile-left");
			const name = mobile ? "--svs-mobile-aspect" : "--svs-aspect";
			video.addEventListener(
				"loadedmetadata",
				() => {
					if (!video.videoWidth || !video.videoHeight) return;
					const ratio = video.videoWidth + " / " + video.videoHeight;
					if (wrapper.style.getPropertyValue(name) === ratio) return;
					wrapper.style.setProperty(name, ratio);
					if (!mobile) {
						wrapper.style.setProperty("--svs-aspect-num", String(video.videoWidth / video.videoHeight));
					}
				},
				{ once: true },
			);
		}

		/* ---------- left column: scroll-scrubbed tall video ---------- */

		setupScrub() {
			const video = this.scrubVideo;

			video.addEventListener("loadedmetadata", () => {
				this.duration = video.duration;
			});
			// Decode the first frame while still off-screen so decoder init never
			// happens mid-scroll.
			video.addEventListener(
				"loadeddata",
				() => {
					if (!this.reducedMotion && video.currentTime === 0) {
						video.currentTime = 0.001;
					}
				},
				{ once: true },
			);
			// The time of the frame **actually presented** on screen. currentTime changes as soon as the seek is issued, but
			// decode/present lags by several frames — during a large backward jump the old frame is still on screen (product lower in the frame),
			// and computing the viewfinder clamp from currentTime clamps too little and cuts off the old frame's product (measured 79px).
			if (typeof video.requestVideoFrameCallback === "function") {
				const onFrame = (_now, meta) => {
					this.presentedTime = meta && typeof meta.mediaTime === "number" ? meta.mediaTime : this.presentedTime;
					video.requestVideoFrameCallback(onFrame);
				};
				video.requestVideoFrameCallback(onFrame);
			}
			this.ensureAspect(video, video.closest(".scroll-video-split__left"));

			// Dock static image layer: while hi-res isn't ready, it covers the video at dock points to provide a lossless frame.
			// Once videoTier advances to "hi" this layer is permanently disabled — the steady state is byte-for-byte identical to today.
			this.holdsLayer = this.querySelector(".scroll-video-split__holds");
			this.holdImgs = this.holdsLayer ? Array.from(this.holdsLayer.querySelectorAll("img[data-hold]")) : [];
			this.shownHold = -1;
			this.videoTier = "none";

			// Frame grid must match the asset's fps, or half the seeks land
			// between frames and repeat the previous one. Current asset
			// (svs-left-512, 2026-07-17 re-export) is 60fps — a design
			// requirement; drop to 1/30 only if a future asset ships at 30fps.
			this.seeker = this.core.createSmoothSeeker(video, { frameStep: 1 / 60, smoothMs: SEEKER_QUANTIZE_ONLY_MS });

			/* Loading is split into two paths (2026-07-27).
			 *
			 * blobLoad is **only usable once fully downloaded** (a streaming src gets throttled, and seeking into an unbuffered range stalls the
			 * decode pipeline, see scroll-video-core.js). Originally there was only one 400% observer triggering the 7.19MB download;
			 * when dragging down quickly, 4 screens amount to less than 1 second of lead time, nowhere near enough.
			 *
			 * The small ones (4 dock images 0.84MB + low-res 0.86MB) start once the first screen is idle — they're not on the critical path,
			 * and the imgs carry fetchpriority="low" and the hi-res blob priority:"low".
			 * The big one (hi-res 7.19MB) still follows viewport lead distance, but raised to 8 screens. */
			const kickSmall = () => {
				this.loadHoldStills();
				this.loadScrub();
			};
			if (typeof requestIdleCallback === "function") {
				requestIdleCallback(kickSmall, { timeout: 3000 });
			} else {
				// Safari supported requestIdleCallback very late, so the fallback can't be omitted — it's precisely the historic hot spot for judder
				setTimeout(kickSmall, 1200);
			}

			this.loadObserver = new IntersectionObserver(
				(entries) => {
					if (entries.some((entry) => entry.isIntersecting)) {
						// Kick it off early if the idle callback hasn't run yet (e.g. dragging to this section right after opening the page)
						kickSmall();
						this.requestHi();
						this.loadObserver.disconnect();
					}
				},
				// 4 screens ahead. ⚠️ On 2026-07-27 this was raised to 800%, reasoning "big files should start downloading early" —
				// with tiering that reasoning is **backwards**: low-res + dock images already cover the experience, and the 7.19MB
				// hi-res is the least urgent thing on the page; downloading it early only competes for bandwidth with the three CDN videos in the right column
				// (measured on hard refresh at 13.7s / 33.7s each). Reverted to the original value.
				{ rootMargin: "400% 0px 400% 0px" },
			);
			this.loadObserver.observe(this);

			// Run the rAF loop only while the section is nearby.
			this.runObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							this.activate();
						} else {
							this.deactivate();
						}
					});
				},
				{ rootMargin: "100% 0px 100% 0px" },
			);
			this.runObserver.observe(this);

			this.removeUnlock = this.core.installTouchUnlock(video, this.reducedMotion);
		}

		/* Downloading the dock images is **not handled here** — the src is written directly in the markup and lazy-loaded by the browser,
		 * because this section's script is defer, and by the time it runs it's already after DOMContentLoaded
		 * (measured 3186ms on this page, while the asset download takes only 25ms). When the user hard-refreshes and immediately drags down, the component doesn't
		 * exist yet, and no "schedule loading in JS" approach can keep up.
		 *
		 * This does just one thing: mark the image that failed to load, so that dock point falls back to showing the video underneath,
		 * never leaving a hole. */
		loadHoldStills() {
			if (!this.holdImgs || this.holdStillsStarted) {
				return;
			}
			this.holdStillsStarted = true;
			this.holdImgs.forEach((img) => {
				// The image may already have finished loading (or even failed) by the time the component upgrades; complete is the reliable indicator
				if (img.complete && !img.naturalWidth) {
					img.dataset.holdFailed = "1";
					return;
				}
				img.addEventListener(
					"error",
					() => {
						img.dataset.holdFailed = "1";
					},
					{ once: true },
				);
			});
		}

		/* The **intent** "time to download hi-res" is expressed by the observer, but actually starting waits until low-res is in place.
		 *
		 * The 7.19MB hi-res is the least urgent thing on the page (low-res + dock images already cover the experience), and shouldn't
		 * start at the same time as the 0.87MB low-res and the three CDN videos in the right column (measured 13.7s / 33.7s each) to compete for bandwidth.
		 * When there's no low-res tier (dataset.srcLow is empty) there's nothing to wait for, so let it through directly. */
		requestHi() {
			this.hiRequested = true;
			const readyToChain = this.videoTier !== "none" || !this.dataset.srcLow;
			if (readyToChain && this.scrubHandle && this.scrubHandle.startHi) {
				this.scrubHandle.startHi();
			}
		}

		loadScrub() {
			if (this.scrubStarted) {
				return;
			}
			this.scrubStarted = true;
			const video = this.scrubVideo;
			video.preload = "auto";
			const hiUrl = this.dataset.src || null;
			// Low-res transitional version (0.86MB, 1/8.3 of hi-res). On 2026-07-16 null was passed on the grounds that "the file is small enough that low-res-first
			// gains nothing" — that judgment was made when the asset was only 3.33MB; now it's 7.19MB, and
			// blobLoad is only usable once fully downloaded, so the premise no longer holds.
			const lowUrl = this.dataset.srcLow || null;
			if (!hiUrl && !lowUrl) {
				return;
			}
			this.scrubHandle = this.core.progressiveAttach(video, {
				lowUrl: lowUrl,
				hiUrl: hiUrl,
				// Hi-res doesn't download right after low-res — it waits until loadObserver (8 screens ahead) says it's time.
				// Without low-res, fall back to the old path (IO trigger downloads hi-res immediately), so the failure fallback holds naturally.
				holdHi: Boolean(lowUrl),
				// swap only happens inside a dock band (static image covering) or while the section is off-screen
				canSwapNow: (waitedMs) =>
					shouldSwapNow({
						inHold: holdBandIndex(this.smoothProgress == null ? -1 : this.smoothProgress, this.holdBands) >= 0,
						sectionActive: Boolean(this.active),
						waitedMs: waitedMs,
						maxWaitMs: 5000,
					}),
				onReady: () => {
					// Already hi, so don't fall back to low (attach is also called on the fallback path)
					this.videoTier = this.videoTier === "hi" ? "hi" : "low";
					// There's a video to paint now, so likewise remove the first-screen guide image
					this.classList.add("is-engine-live");
					// Low-res is in place: if the observer already said "time to download hi-res", only now actually let it through
					if (this.hiRequested) {
						this.requestHi();
					}
					if (this.reducedMotion) {
						video.play().catch(() => {});
					}
				},
				onSwap: () => {
					// Hi-res takes over: the static image layer is permanently disabled; from here behavior is exactly the same as today.
					this.videoTier = "hi";
					this.showHoldStill(-1);
					// After the hi-res swap the frame grid must be re-seeked.
					this.seeker.invalidate();
				},
			});
			this.handles.push(this.scrubHandle);
		}

		/* Show/hide the dock static images. **Hard cut**: the images are bit-exact copies of the frame the engine actually docks on
		 * (asserted bit by bit in tests/unit/svs-dock-stills.test.mjs), so pixels are identical before and after the switch,
		 * and no fade or hysteresis is needed — a cross-fade would actually create an intermediate state of "two identical images
		 * overlaid semi-transparently". */
		showHoldStill(index) {
			if (this.shownHold === index || !this.holdImgs || !this.holdImgs.length) {
				return;
			}
			if (this.shownHold >= 0 && this.holdImgs[this.shownHold]) {
				this.holdImgs[this.shownHold].classList.remove("is-shown");
			}
			// If the image isn't in place, treat it as absent and fall back to showing the video underneath — never leave a hole.
			const img = index >= 0 ? this.holdImgs[index] : null;
			if (img && img.dataset.holdFailed !== "1" && img.complete && img.naturalWidth > 0) {
				img.classList.add("is-shown");
				this.shownHold = index;
				// The engine finally has something to show — remove the pure-CSS first-screen guide image (see the long comment in the CSS).
				this.classList.add("is-engine-live");
			} else {
				this.shownHold = -1;
			}
		}

		activate() {
			if (this.active || this.reducedMotion) {
				return;
			}
			this.active = true;
			this.lastTickTime = 0;
			this.smoothProgress = null;
			this.seeker.reset();
			this.rafId = requestAnimationFrame(this.tick);
		}

		deactivate() {
			this.active = false;
			if (this.rafId) {
				cancelAnimationFrame(this.rafId);
				this.rafId = null;
			}
		}

		// Progress across the whole traversal: 0 when the section top touches
		// the viewport bottom, 1 when the section bottom leaves the viewport
		// top. No sticky involved — the tall video simply plays as it passes.
		progress() {
			const rect = this.getBoundingClientRect();
			const total = rect.height + window.innerHeight;
			if (total <= 0) {
				return 0;
			}
			return Math.min(1, Math.max(0, (window.innerHeight - rect.top) / total));
		}

		/* ---------- scroll→video progress mapping (dock-and-hold) ---------- */

		// While a right-column video is at least `threshold` visible (the same
		// moment it starts looping), the left video holds the frame where the
		// artwork sits level with that item — assuming the asset is a
		// constant-speed top-to-bottom travel, that frame is the item center's
		// fraction of the contain-fitted video box. Between docks the travel is
		// eased so fast flicks read as a drop, not a teleport.
		// "8.4,90.5" → {a: 0.084, b: 0.905}. Anything malformed, inverted or
		// out of range returns null (feature off) rather than a guess — a wrong
		// bound would shift the whole section sideways, which is far worse than
		// not correcting at all.
		parseSubject(raw) {
			if (!raw) {
				return null;
			}
			const parts = String(raw).split(",");
			if (parts.length !== 2) {
				return null;
			}
			const a = parseFloat(parts[0]) / 100;
			const b = parseFloat(parts[1]) / 100;
			if (!isFinite(a) || !isFinite(b) || a < 0 || b > 1 || b - a <= 0.02) {
				return null;
			}
			return { a: a, b: b };
		}

		/* ---------- desktop size model: one number drives both products ----------

		   Solves for --svs-ph (the product's on-screen height) so that ONE screen
		   holds the whole group — copy block, callout pill and both products —
		   and publishes --svs-left-offset so the PAIR (left product's left edge →
		   right phone's right edge) is centred on the viewport.

		   Replaces the old centerPair(), which nudged an already-laid-out grid
		   sideways with a transform. That could only ever fix the horizontal
		   half, and the sizes it was correcting came from two unrelated
		   variables: the left product's size followed the grid COLUMN WIDTH
		   (so it tracked viewport width, then froze at the 1250px max-width)
		   while the right phone's followed --svs-item-height in vh (so it
		   tracked viewport HEIGHT — unless it hit the column, at which point it
		   tracked width again). Three regimes, two subjects, no fixed ratio
		   between them at any resolution.

		   Two constraints now, the tighter one wins:

		     height  the group must fit in innerHeight − header − 2*breathing.
		             MEASURED, not computed: the copy block's height depends on
		             wrapping and webfont metrics, and the pill's overhang above
		             the product depends on the glow anchor in the manifest. So
		             the solver writes a candidate height, lets measureDocks
		             place everything, measures the real group box, and corrects.
		             Converges in 2–3 passes — each pass's residual is only the
		             few percent of the pill overhang that scales with the
		             product.

		     width   the strip carries (leftSubject.a × stripWidth) of empty
		             frame to the LEFT of the product. Centring the pair pushes
		             the strip that far left, and past a point it would leave the
		             viewport — where no clipping we can afford would save it
		             (see the CSS notes on why overflow can't be used here). So
		             the height is also capped by what keeps the strip's left
		             edge on screen. This is the binding constraint on tablets.

		   The right clip is deliberately NOT a constraint: it may overflow and
		   be clipped by the item viewfinder, because everything to the right of
		   the phone is baked-in marketing copy (explicit product decision,
		   2026-07-21 — "don't worry about whether the right-side video gets cut"). */

		headerHeight() {
			// The theme's sticky header covers the top of the viewport, so the
			// group is centred in what's left of the screen, not in all of it.
			const header = document.querySelector(".header-wrapper, sticky-header, .section-header, header");
			if (!header) return 74;
			const rect = header.getBoundingClientRect();
			return rect.height > 0 && rect.height < window.innerHeight / 2 ? rect.height : 74;
		}

		// Real aspect of the right clips once metadata lands, else whatever the
		// merchant typed. How wide a frame has to be to make its phone
		// --svs-ph tall follows from this and the phone's vertical span.
		featureAspect() {
			const feature = this.loopVideos.find(
				(video) => video.classList.contains("scroll-video-split__feature") && video.videoWidth,
			);
			if (feature) {
				return feature.videoWidth / feature.videoHeight;
			}
			const typed = parseFloat(getComputedStyle(this.grid).getPropertyValue("--svs-aspect-num"));
			// 828/1620 — the portrait footage in use since 2026-07-24. (Was
			// 1.4519 = 2352/1620 while the clips still carried baked-in copy.)
			return isFinite(typed) && typed > 0 ? typed : 0.5111;
		}

		// How wide the desktop caption column is in the centering (px). This number is determined by font/line length and independent of product height,
		// so measuring it once per layout is enough; it won't bite back against the solved --svs-ph.
		//
		// What's measured is **the text's ink right edge**, not the column's box width. The column box width is 34ch (--svs-cap-w, the line-length cap),
		// and the longest line almost always falls a bit short of filling 34ch — measured 25px short at a 1540 viewport on 2026-07-25;
		// counting those 25px into the unit means 25px of extra white on the right, which to the eye is "the right side is wider than the left" (user feedback).
		// In other words: 34ch decides **where lines break**, the ink decides **how centering is computed**; they're two separate things.
		//
		// Take the rightmost stroke across all stages / all segments: offset is a single value published on the grid and shared by all dock
		// frames; when caption segments differ in length it must be computed from the widest, otherwise the widest segment overshoots the budget (and centering
		// jumps back and forth between dock frames). Collapsed segments count too — once expanded, that's their width.
		//
		// ⚠️ The .svs-steps entry offset must be temporarily zeroed before measuring: while the caption track pulls out the whole column is translated,
		// and an ink right edge measured with the offset applied would be off (measureDocks uses the same trick to measure capBox).
		// When it can't be measured (the section has no captions), fall back to the column box width, then to 0 (unit = product + phone, same as
		// before 2026-07-25).
		captionWidth() {
			let width = 0;
			this.querySelectorAll(".scroll-video-split__captions").forEach((cap) => {
				const steps = cap.querySelector(".svs-steps");
				const applied = steps ? steps.style.transform : "";
				if (steps) {
					steps.style.transform = "none";
				}
				const left = cap.getBoundingClientRect().left;
				let ink = 0;
				cap.querySelectorAll(".svs-step__h, .svs-step__text-inner").forEach((el) => {
					const range = document.createRange();
					range.selectNodeContents(el);
					const rects = range.getClientRects();
					for (let i = 0; i < rects.length; i++) {
						const rect = rects[i];
						// Line box width = the actual width of that line's text; empty lines / zero-size ones don't count.
						if (rect.width > 2 && rect.height > 2 && rect.right > ink) {
							ink = rect.right;
						}
					}
				});
				if (steps) {
					steps.style.transform = applied;
				}
				const w = ink ? ink - left : cap.getBoundingClientRect().width;
				if (isFinite(w) && w > width) {
					width = w;
				}
			});
			return width;
		}

		// Every width the pair centering needs, expressed per unit of --svs-ph.
		pairMetrics() {
			const aspect = this.featureAspect();
			const vSpan = Math.max(0.2, this.rightSubjectV.b - this.rightSubjectV.a);
			const hSpan = this.rightSubject ? this.rightSubject.b - this.rightSubject.a : 0.821;
			return {
				stripW: 1 / this.leftK,
				// The phone's share of a frame whose height was chosen to make
				// that phone exactly --svs-ph tall.
				phoneW: ((hSpan * aspect) / vSpan) * this.productScale,
				// How much empty strip hangs left of the product.
				productInset: this.leftSubject.a / this.leftK,
				// The product's own width — debug readout only.
				productW: (this.leftSubject.b - this.leftSubject.a) / this.leftK,
			};
		}

		// Width of the header content band = the segment **between** the logo and the right-side account/cart icons (the band where the menu sits),
		// i.e. "the width of `.header--inner` minus the logo and minus the right-side icons". Two things align to it: the copy block
		// (--svs-text-frame, see the publish below) and the frame's width cap (hBand in layout()).
		//
		// Changed twice on 2026-07-24; noted here to avoid flip-flopping again: first this inner band was estimated as "viewport − constant 375",
		// then changed to measure the whole `.header--inner` (logo left edge ↔ icons right edge, i.e. the **outer edges**), and now settled back on
		// the **inner band** — but measured rather than a constant. The benefit of measuring remains: when header settings change (full_width off →
		// shrinks to centered --grid-width) it follows automatically, with no "did we get the constant right" question.
		//
		// Measure the header itself, not the copy block: the copy width now comes from this number, so measuring it would create a loop
		// (the band width would become whatever value happened to be written in the previous frame). The header is the source of truth.
		//
		// Step-by-step fallback: inner band can't be measured (header style changed, logo/icons not found) → the whole
		// `.header--inner`; no header (single-section preview in the theme editor) → the copy block;
		// none of them → null, which callers always treat as "no band constraint".
		// The theme's "maximum page width" (Settings → Design → Maximum page width, written as
		// --grid-width, see snippets/head-variables.liquid). 1280px for this store.
		// Under header--full-width the header band equals the whole viewport, so this is the only source of "the whole unit doesn't exceed the page grid"
		// that actually has a cap (see the pageBand comment in layout()).
		// Read from documentElement rather than this: the variable is defined on :root and readable from any descendant,
		// but reading from the root is least likely to be affected by local overrides.
		gridWidth() {
			const raw = getComputedStyle(document.documentElement).getPropertyValue("--grid-width");
			const px = parseFloat(raw);
			return isFinite(px) && px > 0 ? px : null;
		}

		headerBandWidth() {
			const header = document.querySelector(".header--inner");
			if (header) {
				const logo = header.querySelector(".logolink");
				const icons = header.querySelector(".thb-secondary-area, .thb-header-right");
				if (logo && icons) {
					const band = headerInnerBand(logo.getBoundingClientRect(), icons.getBoundingClientRect());
					if (band > 0) return band;
				}
				const width = header.getBoundingClientRect().width;
				if (width > 0) return width;
			}
			const copy = this.querySelector(".scroll-video-split__text > h3, .scroll-video-split__text > .rte");
			if (!copy) return null;
			const width = copy.getBoundingClientRect().width;
			return width > 0 ? width : null;
		}

		layout() {
			if (!this.grid) {
				return;
			}
			if (!this.isDesktop) {
				// Mobile owns its own stacked/wrapping layout: strip nothing but
				// the desktop-measured values, so a solve from before a viewport
				// change can't leak into it.
				this.grid.style.removeProperty("--svs-ph");
				this.grid.style.removeProperty("--svs-left-offset");
				this.grid.style.setProperty("--svs-shift", "0px");
				this.measureDocks();
				return;
			}
			const vw = this.viewportWidth();
			const vh = window.innerHeight;
			const metrics = this.pairMetrics();
			// Smallest gap between the strip's left edge and the viewport's.
			const EDGE = 20;

			// Width cap. The centred unit is [left product's left edge .. caption
			// column's right edge], so its width is (pairK + STAGE_GAP_K)·H +
			// capW. pairK already includes the pair's column gap — that gap is
			// itself a fraction of the height being solved for (see
			// --svs-pair-gap), so it cannot enter as a fixed px term; the caption
			// column is the opposite (its width is text-driven and does NOT scale
			// with the product), which is why it enters as px — and it is the
			// text's INK width, not the 34ch column box (see captionWidth()).
			// The strip's left
			// edge lands at (vw − unitWidth)/2 − productInset·H and must stay
			// >= EDGE; solving for H gives hMax (see unitWidthCeiling).
			//
			// unitShift (the theme editor's "whole-unit horizontal nudge") turns this constraint into two: shifting right eats the right-side
			// margin, shifting left eats the left-side one. Both have the same form, only the "effective inset" differs — on the left it's
			// inset − shift, on the right it's shift — so feed the tighter of the two into the same pure function;
			// with shift = 0 it falls back to inset, exactly as without this knob.
			//
			// STAGE_GAP_K mirrors .scroll-video-split__stage's
			// gap: calc(--svs-ph * 0.07) — same layout constraint, two halves,
			// change both together.
			const STAGE_GAP_K = 0.07;
			const pairK = metrics.stripW - metrics.productInset + metrics.phoneW + this.pairGap;
			const capW = this.captionWidth();
			const edgeInset = Math.max(metrics.productInset - this.unitShift, this.unitShift);
			const hMax = unitWidthCeiling(vw, pairK, STAGE_GAP_K, capW, edgeInset, EDGE);
			// The page grid band: the copy block's width and (when alignHeaderWidth is on) the whole unit's width
			// cap both come from it. Of the two candidates, take the smaller:
			//   - the header content band (logo right edge ↔ right-side icons left edge)
			//   - the theme's "maximum page width" --grid-width
			// Why min is required: this store's header has header--full-width enabled (`.header--inner`'s
			// max-width is overridden to 100%), so the header band measures 2191px at 2560 — using it as the cap
			// is no cap at all, and the product keeps growing (2026-07-25 feedback: "2560 is too big; the overall layout shouldn't
			// exceed the header menu"). What actually has a max width is --grid-width (theme setting Maximum page
			// width, 1280px for this store). When the header isn't full-width it's larger than the header band, and min() automatically yields to the header band.
			const headerBand = this.headerBandWidth();
			const gridWidth = this.gridWidth();
			const pageBand = headerBand && gridWidth ? Math.min(headerBand, gridWidth) : headerBand || gridWidth;
			if (pageBand) {
				this.grid.style.setProperty("--svs-text-frame", pageBand.toFixed(1) + "px");
			}
			// The whole unit's horizontal budget (second correction on 2026-07-25). The two edges each have their own reference; they're not the same band:
			//   Right edge — pinned to the **body band**'s right edge (pageBand, = the right edge of the description text above, the line the user
			//           drew on the screenshot): text aligned with the frame's right edge is what the eye notices most.
			//   Left edge — only required not to cross the **page grid**'s left boundary (--grid-width, 1280 for this store): the left side is
			//           the strip's empty stretch of frame, visually very light, so letting it sit further left than the body's left edge costs nothing
			//           and actually lets the product be bigger.
			// So budget = right edge − grid left boundary = (body band + grid band) / 2 — independent of the viewport, so
			// the product is the same size at every desktop size (exactly what "don't make it bigger at 2560" asked for).
			// Using only the 1011 body band as the budget, the product would drop to 490px with a big empty stretch below it on screen (measured).
			const gridBand = gridWidth ? Math.min(gridWidth, vw - 2 * EDGE) : null;
			const bandRightX = pageBand ? (vw + pageBand) / 2 : null;
			const unitBudget = bandRightX != null && gridBand ? bandRightX - (vw - gridBand) / 2 : pageBand;
			const band = this.alignHeaderWidth ? unitBudget : null;
			// The cap governs the **whole unit** (product left edge → caption text right edge), no longer just the canvas (strip + viewfinder):
			// without counting the caption column, "the whole unit doesn't exceed the grid" is meaningless (2026-07-25 feedback).
			// unitShift must be counted in the denominator too — after shifting the whole unit right, its right edge pushes out of this band.
			// Only ever a CEILING, never a target. Below MIN_BAND_PRODUCT the
			// viewport simply cannot fit this artwork inside the page grid — the
			// band is 435px on an 810px tablet, which works out to a 207px
			// product — and honouring it there would shrink the products to
			// nothing for no layout benefit. Desktops (>=1280px, where the band
			// yields >=430px) align; tablets and below opt out.
			const MIN_BAND_PRODUCT = 320;
			const hBandRaw = bandHeightCeiling(band, capW, pairK + STAGE_GAP_K, Math.abs(this.unitShift));
			const hBand = hBandRaw >= MIN_BAND_PRODUCT ? hBandRaw : Infinity;

			// The caption column's width budget is no longer a separate cap: it's already counted in hMax above (the capW term).
			// The cost is still the same — on narrow viewports the product gets smaller (everything except mobile is side by side; better small than letting
			// the captions drop below the phone, 2026-07-24 feedback).
			const ceiling = Math.min(hMax, hBand, this.productMax || Infinity);

			const budget = vh - this.headerHeight() - 2 * this.breathing;
			let height = this.productHeight || Math.min(ceiling, budget * 0.55);
			let groupHeight = 0;
			for (let pass = 0; pass < 3; pass++) {
				height = Math.max(120, Math.min(height, ceiling));
				this.grid.style.setProperty("--svs-ph", height.toFixed(1) + "px");
				this.grid.style.setProperty("--svs-ph-scale", String(this.productScale));
				// Dock geometry is measured off boxes this loop is still sizing,
				// hence the interleave: place, measure, correct.
				this.measureDocks();
				groupHeight = this.measureGroup();
				if (!groupHeight) {
					break;
				}
				const delta = budget - groupHeight;
				if (Math.abs(delta) <= 1) {
					break;
				}
				height += delta;
			}
			height = Math.max(120, Math.min(height, ceiling)) * this.productSize;
			this.productHeight = height;
			this.grid.style.setProperty("--svs-ph", height.toFixed(1) + "px");
			// The CSS-side --svs-fw / --svs-item-w read --svs-aspect-num at the
			// GRID level, but the Liquid only emits it per-ITEM — so the grid
			// falls back to the typed constant in the CSS. That constant used to
			// be 1.4519 (the old 2352×1620 landscape clips' ratio); it happened
			// to match the footage, so nobody noticed until a portrait clip
			// (828×1620 → 0.511) got rendered in a landscape viewfinder (box
			// sized 1.4519, video inside 0.511 → backdrop on both sides).
			// featureAspect() already resolves the
			// real ratio (video metadata wins over the typed fallback), so
			// publish it to the grid to keep the CSS box in step with the JS
			// solve. layout() re-runs on each feature clip's loadedmetadata
			// (see the listener wired in setup), so this lands as soon as the
			// real dimensions are known.
			this.grid.style.setProperty("--svs-aspect-num", this.featureAspect().toFixed(4));
			// Re-measure after the trim: the group is now shorter than the
			// budget it was solved against, and the dock alignment below has to
			// centre THAT group, not the full-screen one it started from.
			if (this.productSize < 1) {
				this.measureDocks();
				groupHeight = this.measureGroup() || groupHeight;
			}
			// Distance between the two canvases — a real column gap now, not a
			// nudge inside the viewfinder. Safe because the footage's backdrop
			// and the page's background are the same colour (see the CSS), and
			// therefore unbounded: it no longer borrows from the footage's own
			// margin, so it holds its size however small the products get.
			const slide = Math.max(0, this.pairGap * height);
			this.grid.style.setProperty("--svs-pair-gap", slide.toFixed(1) + "px");

			// Unit centering. Every box is px now (the columns stopped being fr
			// fractions), so this is a direct solve rather than the old
			// measure → shift → clamp → remeasure dance: place the strip exactly
			// where [product left edge .. CAPTION column right edge] straddles
			// the viewport midline. The EDGE floor gives up a little centering
			// rather than let the strip walk off screen — it only binds when the
			// width cap above was already saturated.
			//
			// 2026-07-25: the unit used to stop at the phone's right edge, with
			// the captions living in the leftover right-hand whitespace — so the
			// artwork read as sitting right of centre. The phone and its captions
			// are one block now, and THAT block is what gets centred against the
			// left product.
			// Horizontal placement (finalized 2026-07-25): the whole unit's **right edge aligns with the body block's right edge**, rather than centering the whole
			// unit. Body block = the page grid band (pageBand, centered in the viewport), so its right edge is at
			// (vw + band)/2. The target line the user drew on the screenshot is exactly the description text's right edge —
			// "move the right side over a bit for me" wanted this alignment, not another percentage tweak.
			//
			// Why align the right edge rather than center: the whole unit is narrower than the body band (1020 vs 1171 at 1540); centering
			// would leave a little on each side, aligned with the text above on neither; with right-edge alignment all the slack lands on the left — and the left
			// happens to be the strip's empty stretch of frame, visually "light" already, so it reads as aligned rather than off.
			// This also explains the motive behind the earlier rounds of repeatedly nudging unitShift right (that was manually approximating this line).
			//
			// When the band can't be measured / the toggle is off, fall back to strict centering (old behavior). unitShift remains as a
			// manual nudge on top of this (default 0), following height rather than px, so it looks the same at any resolution.
			const unitWidth = (pairK + STAGE_GAP_K) * height + capW;
			const centredOffset = (vw - unitWidth) / 2 - metrics.productInset * height;
			// Alignment uses the **body band's right edge** (bandRightX), not the budget band above — the budget band only decides
			// "how wide it can be"; where the right edge lands is decided by the text.
			const alignedOffset =
				bandRightX != null && this.alignHeaderWidth ? bandRightX - unitWidth - metrics.productInset * height : null;
			const baseOffset = alignedOffset == null ? centredOffset : alignedOffset;
			// Two floors: left (strip doesn't go off screen) and right (captions don't touch the screen's right edge). hMax above already
			// counts both sides into the cap; this is a fallback — when the cap is held down by other constraints (hBand / productMax),
			// alignment or shift could still push one side out.
			const rightFloor = vw - EDGE - unitWidth - metrics.productInset * height;
			const offset = Math.min(Math.max(EDGE, baseOffset + this.unitShift * height), Math.max(EDGE, rightFloor));
			this.grid.style.setProperty("--svs-left-offset", offset.toFixed(1) + "px");
			this.grid.style.setProperty("--svs-shift", "0px");

			// Where a docked clip must rest for the GROUP — not the clip — to be
			// vertically centred: the group's top edge sits at header +
			// breathing, so the item's centre lands that far down plus whatever
			// copy/pill stack precedes it. The authored dock_align_offset is
			// added to this as a manual trim (see measureDocks).
			const itemHeight = height * this.productScale;
			const centreY = this.headerHeight() + this.breathing + (groupHeight || budget) - itemHeight / 2;
			this.dockAlignAuto = Math.min(0.45, Math.max(-0.45, centreY / vh - 0.5));

			// Nothing but this block on screen for the WHOLE time the artwork is
			// held. The right column is not pinned — the 2026-07-07 baseline tried
			// that and reverted it — so a dock is a window of FREE SCROLL, 40% of
			// the viewport at the authored default. Three things have to hold
			// across all of it, while the group travels T px up the viewport:
			//
			//   product whole      itemBottom       <= vh - M     (worst: window start)
			//   neighbour hidden   itemBottom + gap >= vh + M     (worst: window end)
			//   headline clear     itemBottom - group >= header + M
			//
			// Writing B for the item's bottom at the window's centre, the first
			// two give gap >= T + 2M, the outer two gap <= span - T - 2M with
			// span = vh - header - baseGroup. Both hold at once only while
			// T <= span/2 - 2M, and at that T the rest point is pinned to
			// B = vh - M - T/2. So the gap, the dock's travel and where the group
			// parks are ONE solve, not three settings — the authored dock window
			// becomes a ceiling on T rather than the value itself.
			//
			// Solving only the gap (2026-07-21) left a band exactly one gap wide
			// where all three held, inside a window five times as long: it measured
			// clean at that band and showed the next headline the moment you
			// scrolled on ("at certain sizes you can still see the text at the bottom", 2026-07-22).
			const rightColumn = this.querySelector(".scroll-video-split__right");
			// Slack at each of the three edges. The 2026-07-21 solve aimed for
			// zero and landed on the wrong side of it once real fonts moved the
			// group a few px.
			const MARGIN = 10;
			this.grid.style.setProperty("--svs-item-gap-min", "0px");
			this.measureDocks();
			const baseGap = rightColumn ? parseFloat(getComputedStyle(rightColumn).rowGap) || 0 : 0;
			const baseGroup = (this.measureGroup() || budget) - baseGap;
			const span = vh - this.headerHeight() - baseGroup;
			let travel = Math.max(0, span / 2 - 2 * MARGIN);
			// The gap has to hide a neighbour on BOTH sides. The first cut only
			// covered the one below; the block ABOVE stayed visible under the header
			// for the first third of the window (23-40px of a phone, 2026-07-22).
			// Adding it gives gap >= (span + 2M + T)/3 — and the joint solve still
			// bottoms out at the same T <= span/2 - 2M, so guarding the second
			// neighbour costs dock window nothing.
			const gapFloor = Math.max(0, travel + 2 * MARGIN, (span + MARGIN + travel) / 3);
			this.grid.style.setProperty("--svs-item-gap-min", gapFloor.toFixed(1) + "px");
			this.measureDocks();
			groupHeight = this.measureGroup() || groupHeight;
			// baseGroup took every group to grow with the gap, which only holds
			// for the copy-topped ones: where the callout pill hangs highest the
			// group keeps its height and the estimate runs long. Re-derive the
			// travel from the settled measurement and keep the tighter of the two.
			// The gap is left as solved — wider than the travel only ever helps.
			// Re-derive the travel from the SETTLED measurement. baseGroup assumed
			// every group grows with the gap, which only holds for the copy-topped
			// ones — where the callout pill hangs highest the group keeps its height
			// and the estimate runs long, which showed up as both neighbours peeking
			// 6-15px at the ends of the window. These are the four constraints below
			// rearranged for T, using the real group height and the real gap, so the
			// bracket [lo, hi] that follows is guaranteed non-empty.
			const gapNow = Math.max(baseGap, gapFloor);
			const headerNow = this.headerHeight();
			travel = Math.max(
				0,
				Math.min(
					travel,
					// product whole vs headline clear
					vh - headerNow - 2 * MARGIN - groupHeight,
					// product whole vs block above hidden
					gapNow - 2 * MARGIN,
				),
			);
			this.dockTravel = travel / vh;
			// Rest point: the item's bottom at B, so the window's near end still
			// clears the fold and its far end still hides the next block.
			//
			// dockAlignOffset comes off here because measureDocks adds it back on
			// the way out. It is a hand trim from the layout that preceded this
			// solver — 14% on the product page, which parked the group 112px low
			// and hung the product's bottom past the fold — and with the rest point
			// now solved exactly there is nothing left for it to correct, so it is
			// cancelled rather than clamped. Retuning it in the theme editor no
			// longer moves this section.
			// Rest point. Four things must hold across the whole window, with the
			// group sweeping T px up it:
			//
			//   product whole      B + T/2 <= vh - M
			//   headline clear     B - T/2 - group >= header + M
			//   block below hidden B - T/2 + gap >= vh + M
			//   block above hidden B + T/2 - group - gap <= header - M
			//
			// Those bracket B rather than pin it, and the first cut took the top of
			// the bracket — the item's bottom hard against the fold. The group then
			// hugged the bottom exactly when the callout lit up (the callout opens at
			// the window's START), leaving a hole above it: 126px of air over the
			// headline against 10px under the product ("the top position is too high… what I wanted before was to
			// see the annotation roughly centered on screen", 2026-07-22). Taking the MIDDLE of
			// the bracket balances the two margins instead, at no cost to any of the
			// four constraints — they are all still satisfied, just not at equality.
			const lo = Math.max(headerNow + MARGIN + groupHeight + travel / 2, vh + MARGIN + travel / 2 - gapNow);
			const hi = Math.min(vh - MARGIN - travel / 2, Infinity);
			// hi < lo means this viewport cannot satisfy all four at once; the
			// midpoint is then the least-violation compromise rather than a solution.
			// Aim for the group centred in the space under the header, then clamp
			// into the feasible bracket. Taking the bracket's midpoint instead only
			// centres when the bracket happens to be symmetric — drop one constraint
			// and the midpoint slides straight back to the bottom-hugging position.
			const centred = vh - (vh - headerNow - groupHeight) / 2;
			const restBottom = Math.min(hi, Math.max(lo, centred));
			this.dockAlignAuto = Math.min(
				0.45,
				Math.max(-0.45, (restBottom - itemHeight / 2) / vh - 0.5 - this.dockAlignOffset),
			);
			this.measureDocks();

			if (this.debugReadout) {
				console.log("[svs] size model", {
					productHeight: Math.round(height),
					scale: Math.round(this.productSize * 100) + "%",
					heightBudget: Math.round(budget),
					measuredGroupHeight: Math.round(groupHeight),
					widthCap: hMax === Infinity ? "-" : Math.round(hMax),
					limitedBy:
						height < ceiling - 1
							? "height (doesn't fit in one screen)"
							: hBand < hMax
								? "page grid (whole unit no wider than min(header band, --grid-width))"
								: "width (any bigger and the left strip goes off screen)",
					pageGridBand: band ? Math.round(band) : "disabled/unmeasurable",
					headerBandWidth: headerBand ? Math.round(headerBand) : "-",
					gridWidth: gridWidth ? Math.round(gridWidth) : "-",
					leftStripWidth: Math.round(metrics.stripW * height),
					leftProductWidth: Math.round(metrics.productW * height),
					rightPhoneWidth: Math.round(metrics.phoneW * height),
					captionColumnWidth: Math.round(capW),
					unitBoundingWidth: Math.round(unitWidth),
					leftColumnStart: Math.round(offset),
					autoDockOffset: (this.dockAlignAuto * 100).toFixed(1) + "%",
					viewport: vw + "x" + vh,
				});
			}
		}

		// item is now wrapped by .scroll-video-split__stage (phone + right-side captions in one row).
		// Anywhere that needs to find "the previous copy block" or "which box gets the margin" must start
		// from the stage — item's previousElementSibling is now null or the mobile phone slice.
		stageOf(item) {
			return item.closest(".scroll-video-split__stage") || item;
		}

		// Height of the tallest "one screen" group: from the top of whatever
		// sits above an item — its copy block, or the callout pill when that
		// floats higher — down to the bottom of the item itself. Measured, not
		// derived: see layout()'s header comment for why.
		measureGroup() {
			const items = Array.from(this.querySelectorAll(".scroll-video-split__item"));
			if (!items.length) {
				return 0;
			}
			let tallest = 0;
			items.forEach((item, index) => {
				const rect = item.getBoundingClientRect();
				if (rect.height <= 0) {
					return;
				}
				let top = rect.top;
				const previous = this.stageOf(item).previousElementSibling;
				if (previous && previous.classList.contains("scroll-video-split__text")) {
					top = Math.min(top, this.outerBox(previous).top);
				}
				// The pill hangs above the product by the leader line plus the
				// glow's offset from the dock — often higher than the copy block,
				// and it is just as much part of what has to fit on the screen.
				const annotation = this.annotations.find((entry) => parseInt(entry.el.dataset.dockIndex, 10) === index + 1);
				const label = annotation && annotation.el.querySelector(".scroll-video-split__annotation-label");
				if (label) {
					const pill = label.getBoundingClientRect();
					if (pill.height > 0) {
						top = Math.min(top, pill.top);
					}
				}
				tallest = Math.max(tallest, rect.bottom - top);
			});
			return tallest;
		}

		measureDocks() {
			// Mobile has no scrub column to map (setupScrub was skipped); the
			// paired loop clips play ungated via the visibility observer.
			if (!this.isDesktop) {
				this.mapPoints = null;
				this.clearPopOut();
				return;
			}
			const items = Array.from(this.querySelectorAll(".scroll-video-split__item"));
			// Both annotation guards (see the end of this method) are recomputed
			// from the authored geometry on every pass, so last viewport's
			// leftovers must not leak into this measurement. The clearance is
			// held across the second pass on purpose — that pass exists to
			// re-measure WITH it applied.
			if (!this.clearancePass) items.forEach((item) => this.stageOf(item).style.removeProperty("--svs-anno-clearance"));
			const video = this.scrubVideo;
			if (!items.length || !video) {
				this.mapPoints = null;
				return;
			}
			const sectionRect = this.getBoundingClientRect();
			const viewportHeight = window.innerHeight;
			if (sectionRect.height <= 0 || viewportHeight <= 0) {
				this.mapPoints = null;
				return;
			}

			// Fit the displayed strip the same way CSS object-fit does, so dock
			// frames are fractions of the box the visitor actually sees. cover
			// (Math.max) fills the column and overflows the sides; contain
			// (Math.min) shows the whole frame and letterboxes. The single
			// offset line handles both: displayHeight ≥ column (cover) shifts the
			// origin up, ≤ column (contain) shifts it down.
			// Measure the WRAPPER, not the video: the levelling translateY on
			// the video is included in its own rect, so a mid-scroll remeasure
			// (hi-res swap metadata, debounced resize) would bake the currently
			// applied offset into videoTop — and every dock offset and
			// annotation anchor downstream would land shifted by exactly that
			// amount. The video fills the wrapper (width/height 100%), so the
			// wrapper rect IS the untransformed video box.
			const leftBox = (video.parentElement || video).getBoundingClientRect();
			let videoTop = leftBox.top - sectionRect.top;
			let videoHeight = leftBox.height;
			// Horizontal fit, for glow-anchored annotations: when cover crops
			// the strip's sides, a strip x% is not a column x% — videoLeft can
			// go negative (crop) or positive (contain pillarbox).
			let videoLeft = 0;
			let videoWidth = leftBox.width;
			// Frame geometry comes from the shared helper — section-handoff
			// resolves its take-off rect with the same call, so the two can no
			// longer drift apart.
			const frame = this.core.stripFrame(video, leftBox);
			videoTop = frame.top - sectionRect.top;
			videoHeight = frame.height;
			videoLeft = frame.left - leftBox.left;
			videoWidth = frame.width;
			if (videoHeight <= 0) {
				this.mapPoints = null;
				return;
			}

			const total = sectionRect.height + viewportHeight;
			// The seek target is mapped * (duration - 0.05): compensate that
			// tail here so the held frame shows the artwork exactly at the
			// spatial dock fraction (without this the artwork lands ~0.6%
			// early — tens of pixels on a tall strip).
			const timeScale = this.duration ? this.duration / Math.max(0.001, this.duration - 0.05) : 1;

			// Dock while the item's center sits inside a viewport-centered band
			// (--dock window, default 40vh). Deliberately NOT tied to the play
			// threshold: a low threshold once widened the windows past the gap
			// between items, collapsing the travel segments to zero scroll
			// distance — the artwork teleported and read as a hard stutter on
			// fast flings.
			const centers = items.map((item) => {
				const rect = item.getBoundingClientRect();
				const offset = rect.top - sectionRect.top;
				return {
					// viewportHeight/2 is the rest point; subtracting the offset
					// here moves the DOCKED CLIP down by that many pixels (the
					// progress that parks it arrives that much earlier).
					centerP:
						(viewportHeight / 2 -
							(this.dockAlignOffset + this.dockAlignAuto) * viewportHeight +
							offset +
							rect.height / 2) /
						total,
					// Item center in section coordinates (px) — the alignment
					// target the manifest offset moves the held product onto.
					centerY: offset + rect.height / 2,
					dockV: Math.min(1, Math.max(0, (offset + rect.height / 2 - videoTop) / videoHeight)),
					// Progress band while the item is FULLY inside the viewport:
					// from "bottom edge has entered" to "top edge is about to
					// leave". Annotations gate on this so the callout only shows
					// once the whole right video is on screen.
					fullStart: (offset + rect.height) / total,
					fullEnd: (viewportHeight + offset) / total,
				};
			});
			// The authored window is a ceiling: layout() solves how much free
			// scroll this viewport can actually cover without a neighbouring block
			// coming into view, and that is usually the smaller of the two.
			const dockSpan = Math.min(this.dockWindow, this.dockTravel);
			const baseHalf = (dockSpan / 2) * (viewportHeight / total);
			const points = [{ p: 0, v: 0 }];
			const playGates = new Map();
			const popWindows = [];
			const capWindows = [];
			// Second map sharing the SAME p breakpoints: scroll progress →
			// vertical offset (px) applied to the strip via translateY. At each
			// dock it holds the value that puts the manifest's product position
			// level with the right item; between docks it interpolates linearly.
			// Inside a dock window BOTH maps are flat and the footage itself is
			// a still — nothing moves, so the offset can't expose frame-grid
			// jitter (the failure mode that killed the 2026-07-07 viewport
			// mechanisms; see the design doc's revival condition).
			const offsets = [{ p: 0, v: 0 }];
			const docks = [];
			const leftTop = leftBox.top - sectionRect.top;
			let lastV = 0;
			centers.forEach((entry, i) => {
				const manifest = this.dockManifest ? this.dockManifest[i] || null : null;
				// Never let a window eat more than 60% of the run-up to a
				// neighbouring dock — travel keeps >= 40% of the span so the
				// fall always has real scroll distance to happen in.
				let half = baseHalf;
				if (i > 0) half = Math.min(half, 0.3 * (entry.centerP - centers[i - 1].centerP));
				if (i < centers.length - 1) half = Math.min(half, 0.3 * (centers[i + 1].centerP - entry.centerP));
				const pStart = Math.min(1, Math.max(0, entry.centerP - half));
				const pEnd = Math.min(1, Math.max(0, entry.centerP + half));
				// Both dock kinds express the hold as a fraction of the asset
				// duration, so the forward-only guard compares them directly.
				const dockFraction = manifest ? manifest.t : entry.dockV;
				if (pEnd <= pStart || dockFraction < lastV) return;
				// Geometric mode only: never rest deeper than the configured
				// travel cap (keeps the last dock on a frame where the product
				// is still whole). Authored holds are exact frames picked by the
				// asset's author — capping them would land between scenes.
				const cappedDockV = manifest ? manifest.t : Math.min(entry.dockV, this.dockMax);
				const dockMapped = Math.min(1, cappedDockV * timeScale);
				// Annotation visibility = dock window ∩ item fully visible: the
				// callout appears only after the right video has completely
				// entered the viewport, and hides as soon as it starts to leave.
				// If the item is taller than the viewport the intersection is
				// empty — fall back to the dock window so the callout still shows.
				let showStart = Math.max(pStart, entry.fullStart);
				let showEnd = Math.min(pEnd, entry.fullEnd);
				if (showEnd <= showStart) {
					showStart = pStart;
					showEnd = pEnd;
				}
				// The pull-out is clamped to the DOCK end, not to the lingering
				// callout: the panel must finish arriving while the artwork is
				// still parked, or the cause is gone before the effect settles.
				const dockShowEnd = showEnd;
				// Never shorter than the guaranteed share of a screenful, and never
				// so long that it runs into the next dock (where the pill would be
				// anchored to artwork that has moved on entirely).
				// +4px so the callout is still up AT the stated fraction, not merely up
				// TO it: landing exactly on showEnd toggles it off, and on a 1080p
				// screen the sample at exactly 15% caught it already gone.
				const guaranteed = showStart + (this.annoMinVh * viewportHeight + 4) / total;
				// Halfway to the next dock at most. Each dock's own half-window is
				// capped at 0.3x the spacing, so its pStart sits at 0.7x — stopping
				// at 0.5x keeps the frozen stretch below from ever running into it
				// and leaving the progress maps non-monotonic.
				const nextLimit = centers[i + 1] ? (entry.centerP + centers[i + 1].centerP) / 2 : 1;
				showEnd = Math.min(1, nextLimit, Math.max(showEnd, guaranteed));
				// Hold the frame for as long as the CALLOUT is up, not merely for
				// the dock's composition window. The pill's anchor is written once
				// per layout while the strip is re-levelled every frame, so the two
				// only agree while both maps are flat — the moment the artwork moves
				// again the pill is pointing at where the product used to be. That
				// is invisible between docks (the levelling deltas are small) and
				// glaring after the LAST one, where the ending ramps the strip by
				// parkOffset, ~-578px here ("only the last one has this problem, especially obvious",
				// 2026-07-22).
				//
				// Freezing longer costs nothing structurally: the composition
				// constraints that cap the dock window govern where the RIGHT column
				// sits, and the right column keeps scrolling whatever the strip is
				// doing. The only price is that the fall between docks is squeezed
				// into correspondingly less scroll.
				const holdEnd = Math.max(pEnd, showEnd);
				points.push({ p: pStart, v: dockMapped });
				points.push({ p: holdEnd, v: dockMapped });
				// Level the held product with the right item: the manifest says
				// where the product sits inside the strip at the hold frame, the
				// layout says where the item center is — the difference is the
				// offset. Both sides are re-measured per layout, so the pairing
				// survives any viewport size without hand-tuned percentages.
				const offsetPx = manifest ? entry.centerY - (videoTop + manifest.y * videoHeight) : 0;
				offsets.push({ p: pStart, v: offsetPx });
				offsets.push({ p: holdEnd, v: offsetPx });
				// Where the docked artwork sits inside the left column, as a
				// fraction of the column box — dock-bound annotations anchor here
				// so they are always level with the artwork and the right item.
				// Manifest mode: the offset above moves the product exactly onto
				// the item center, so the anchor IS the item center — unless the
				// entry carries a glow point, in which case the anchor is the
				// glow itself (the product center plus the authored glow-center
				// delta, both axes through the same fit math). anchorX null =
				// leave the block's own x setting alone.
				const hasGlow = manifest && manifest.gx != null;
				docks.push({
					// Frame-freeze band: points / offsets each pushed two equal-valued breakpoints at [pStart, holdEnd],
					// so both mappings are flat over this segment. Dock static images show/hide by this band — note they **must not** use the
					// showStart/showEnd below, which is the annotation window (also with an annoMinVh floor), narrower than and offset from the freeze band;
					// using it would cut images while the product is still moving.
					pStart: pStart,
					holdEnd: holdEnd,
					showStart: showStart,
					showEnd: showEnd,
					anchor: manifest
						? (entry.centerY + (hasGlow ? (manifest.gy - manifest.y) * videoHeight : 0) - leftTop) / leftBox.height
						: (videoTop - leftTop + cappedDockV * videoHeight) / leftBox.height,
					anchorX: hasGlow ? (videoLeft + manifest.gx * videoWidth) / leftBox.width : null,
				});
				// Pull-out window, in the same progress units. It STARTS at the
				// callout (see popLead): the press is announced, and the panel is
				// then thrown out of the product — cause, then effect.
				//
				// It also cannot outlast the callout. The authored run is only a
				// CEILING: the real length is whatever the callout is on screen
				// for, because the callout's window is the dock window, which the
				// one-screen solve had to squeeze to ~53-93px depending on the
				// viewport. Left uncapped at the authored 190px the callout blinked
				// out with the panel still 140px of scroll from home, and the two
				// halves of the beat never shared the screen ("the annotation shows for too
				// short a time… I want to see both fully", 2026-07-22). Buying a longer NATIVE dock
				// window means a smaller product; that was the only currency until
				// the anno_min floor landed (showEnd = max(window, showStart +
				// annoMinVh·vh), line ~1116). Since then the beat is guaranteed
				// independent of product_size, so the product can run large
				// (default 90%, 2026-07-23) without shortening the callout.
				//
				// Travel is horizontal only. At a dock the artwork is levelled onto
				// the item's centre (see the offsets map above), so the product's
				// centre and the item's centre share a row and the vertical delta is
				// zero — also the calmer motion the prototype settled on. The
				// horizontal delta is constant per layout: both columns sit in the
				// same flow and scroll together, so it is measured once here instead
				// of forcing a rect read every frame.
				// Measure the item WITHOUT its pull-out transform. Reading the
				// live rect while a translate is applied makes dx come out as
				// (true dx − already-applied shift), and since every relayout
				// feeds that back in, dx decays to ~0 within a few passes and
				// the item stops sliding at all. Same class of mistake as
				// measuring the scrub video instead of its wrapper (see above).
				// Hoisted **outside** the pull-out branch: the caption track "pull-out" below must use the same untransformed
				// box to find the phone's right edge, rather than measuring separately (the transform state differs between two measurements).
				let itemBox = null;
				if (items[i]) {
					const applied = items[i].style.transform;
					items[i].style.transform = "none";
					itemBox = items[i].getBoundingClientRect();
					items[i].style.transform = applied;
				}
				let popEnd = showStart;
				if (this.popOut && items[i]) {
					// left_subject spans the product inside the strip; its midpoint
					// is the point the item slides out of.
					const productMidX = leftBox.left + ((this.leftSubject.a + this.leftSubject.b) / 2) * leftBox.width;
					const popStart = Math.max(0, Math.min(dockShowEnd, showStart + this.popLead / total));
					popEnd = Math.min(dockShowEnd, popStart + this.popRun / total);
					popWindows.push({
						item: items[i],
						start: popStart,
						end: popEnd,
						dx: productMidX - itemBox.left,
					});
				}
				// Second stage of the three: only after the phone has settled (popEnd) does the caption track pull out of the phone's right edge as a whole.
				// The window is **not** clamped to dockShowEnd — after the one-screen solve the dock window is only ~84px, and the pull-out
				// alone already fills it (popEnd often equals dockShowEnd exactly); clamping again would yield a
				// zero-length window, with the captions flashing in instantly instead of sliding out. It runs on the way to the next dock: the right column's
				// motion is unrelated to whether the product is still parked, so there's no visual cost.
				let capIdx = null;
				// What moves is .svs-steps, not the whole .scroll-video-split__captions: the ending effect
				// layer .svs-fx is another grid child of captions, and its icons are absolutely positioned resolving upward to
				// the stage; giving captions a transform / position / z-index would drag the effect out of place
				// (measured 2026-07-24: the icons ran onto the caption track). .svs-steps and .svs-fx are siblings,
				// so moving it doesn't affect the other.
				const capEl = items[i] && items[i].parentElement ? items[i].parentElement.querySelector(".svs-steps") : null;
				// Desktop only: on mobile the captions are a horizontal bar **below** the phone, so pulling out from the right makes no sense.
				// Uses the same width gate as gatedLoop() (mqDesktop); the two must not judge separately.
				// Tracks already pulled out (dataset marker) don't get a new window: measure re-runs on resize,
				// otherwise every re-measure would stuff already-placed captions back behind the phone and replay it again.
				// reducedMotion is **not** in the condition: that branch also needs a window to release the captions,
				// it's just visible immediately on release, without the animation (see triggerCaptionReveal).
				if (capEl && itemBox && this.mqDesktop.matches && !capEl.dataset.svsCapRevealed) {
					const capApplied = capEl.style.transform;
					capEl.style.transform = "none";
					const capBox = capEl.getBoundingClientRect();
					capEl.style.transform = capApplied;
					capIdx = capWindows.length;
					capWindows.push({
						el: capEl,
						// The right-side video this track follows: the pull-out also waits until it has a frame to paint, otherwise the captions would run
						// ahead of the video (see capCanReveal).
						video: items[i].querySelector(".scroll-video-split__feature"),
						// Trigger point (scroll), not an animation range: once crossed it runs to completion on its own over capRevealMs.
						at: popEnd,
						// Negative: the captions are first pushed to the phone's right edge (left edge coinciding with the viewfinder's right edge — the viewfinder's
						// width is the phone width, see --svs-item-w), then slide back to 0, reading as "pulled out of the
						// video". CSS guarantees via z-index that it sits under the viewfinder.
						dx: itemBox.right - capBox.left,
						fired: false,
					});
					// Stuff it back behind the phone right after measuring — it has to be hidden before the trigger. The scroll-driven version wrote
					// this starting state every frame (at progress 0 it naturally equals hidden); after switching to time-driven it's only written at the trigger moment,
					// hence "the right video hasn't come out yet, but the captions are already hanging there" (2026-07-24 feedback).
					// The same starting state is written again on trigger with a forced reflow; the duplication is intentional: that one is for
					// the case where measure and the first tick collide in the same frame (the browser never painted the starting state).
					//
					// "Hide right after measuring" won't hide the captions forever: the runObserver that runs tick has a rootMargin of
					// 100% above and below, so rAF is already running when the section is still a screen away from the viewport, and by the time it's actually visible
					// the trigger has long since been evaluated. deactivate() only stops rAF and doesn't touch styles; scrolling back, activate()
					// resumes evaluating (the trigger condition is a threshold, not a range, so however fast you skim past it won't be missed).
					capEl.style.transition = "none";
					// Under reducedMotion the offset starting state isn't written: that branch becomes visible immediately on release, with no
					// "slide back to 0" step; leaving the offset would make the captions jump hard back from the offset position.
					if (!this.reducedMotion) {
						capEl.style.transform = "translateX(" + (itemBox.right - capBox.left).toFixed(1) + "px)";
					}
					capEl.style.opacity = "0";
				}
				if (manifest) {
					const loop = items[i].querySelector(".scroll-video-split__feature");
					if (loop) {
						// Play gate:
						//   Captions not yet pulled out (capIdx set and not done) → gate **shut tight**, opened when the caption
						//     pull-out animation finishes — the last stage of the three: "play only after the captions are out".
						//     The pull-out is time-driven, not a scroll position, so no threshold can be given here.
						//   Already pulled out / no caption track / mobile / reduced-motion
						//     → use show (the moment the annotation appears, the old gate set on 2026-07-23).
						// Why it must be split in two: the pull-out plays only once per clip; when scrolling back up and down again
						// the captions are already in place with no animation to wait for, and if the gate were still waiting for it it would never open,
						// reading as "it doesn't play after scrolling back" (measured 2026-07-24).
						playGates.set(loop, { show: showStart, capIdx: capIdx });
					}
				}
				lastV = dockFraction;
			});
			// Opening hold (manifest mode): park frame 0 — product at the strip
			// top — until the strip top is about to reach the viewport center.
			// p starts advancing while the section is still below the fold, and
			// a scene-cut asset's fast first transition outruns the reveal:
			// without this hold the visitor only ever sees an empty strip top
			// and a product that is already down at dock 1. Constant-speed
			// assets track the reveal edge naturally, so geometric mode keeps
			// its straight lead-in. The 0.6 clamp keeps >= 40% of the first
			// dock's run-up as real travel distance (same rule as the windows).
			// Entry reveal (manifest mode): the strip stays invisible until the
			// section top has climbed to the viewport's half line, materializes
			// at 0.3 opacity — a quiet entrance, not a hard cut — and reaches
			// full opacity by the first dock. A step at pAppear (duplicate p)
			// is intentional: 0 → 0.3 of a translucent object reads softly,
			// while ramping from 0 would keep it near-invisible for most of the
			// short window. Position function → scrolling back up fades it out
			// symmetrically.
			const opacities = [{ p: 0, v: 0 }];
			if (this.dockManifest && points.length > 1) {
				const pLead = Math.min(Math.max(0, (viewportHeight / 2 + videoTop) / total), points[1].p * 0.6);
				points.splice(1, 0, { p: pLead, v: 0 });
				// Frame AND offset stay frozen through the park together — same
				// zero-jitter rule as the docks. Entry hang (restored 2026-07-26): this segment's
				// offset lifts the whole strip, so the product hangs from the previous module's bottom edge and then falls. The curve's **start
				// and pLead are written with the same number**, which is what makes this segment constant — changing only pLead would make 0..pLead
				// interpolate, and the product would slide all the way down during the hold segment, exactly what "hold" is meant to avoid.
				// On 2026-07-25 it was welded to 0 (on the grounds that the left column had become a clipping viewfinder), but the left column's
				// clip-path has -800px of slack and can draw outside; the consequence of welding it was that the product only appeared inside the section.
				const entryLift = entryLiftOffset({
					manifest: this.dockManifest,
					duration: this.duration,
					frameHeight: videoHeight,
					liftFrac: this.entryLift,
				});
				offsets[0].v = entryLift;
				offsets.splice(1, 0, { p: pLead, v: entryLift });
				// Appear no later than the fall starts — an invisible product
				// falling and materializing mid-drop would read as a glitch.
				const pAppear = Math.min(viewportHeight / 2 / total, pLead);
				opacities.push({ p: pAppear, v: 0 });
				opacities.push({ p: pAppear, v: 0.3 });
				opacities.push({ p: points[2].p, v: 1 });
			}
			// The tail of this curve is appended after pDone is known (search
			// "hand the product over"): the strip has to fade before it starts
			// covering the next section.

			// Tail. Geometric mode HOLDS on the last dock's frame while the
			// section scrolls out — constant-speed assets keep travelling in
			// their last stretch, and playing that out made a second product
			// appear to slide in at the very end. Manifest mode PLAYS the
			// authored ending (glow off, product settling into a final still),
			// but must FINISH while its stage is still on screen: the ending is
			// staged at the strip bottom, and stretching the playback all the
			// way to p=1 meant it ran entirely in the zone already covered by
			// the next section (only the section's top remains visible near
			// p=1).
			//
			// So v hits 1 when the strip bottom — or the section bottom, whichever
			// comes first — reaches the viewport bottom: the ending plays out
			// fully on screen, and the final still then rides up frozen. Under
			// cover the blown-up strip's bottom hangs BELOW the section and is
			// clipped, so it would never show there — hence the earlier of the
			// two edges. When even that lands before the last dock closes (the
			// usual case: the last right item sits near the section bottom),
			// the ending simply splits the leftover runway with whatever comes
			// after it.
			//
			// parkProgress is published for section-handoff.js: the product's
			// flight into the next section must not take off before the
			// footage is parked on its authored last frame, or the ghost
			// leaves while the artwork is still mid-turn ("by the last module it's
			// tilted", 2026-07-17). The flight's own lead cannot express this —
			// it is measured from the NEXT section's pin, and no value in its
			// range happens to clear this section's last dock — so the two are
			// coupled explicitly instead of by hand-tuned numbers.
			let parkOffset = 0;
			if (this.dockManifest) {
				const lastP = points[points.length - 1].p;
				let pDone = Math.min(videoTop + videoHeight, total - viewportHeight) / total;
				// Preferred point unreachable (the last item sits so low that the
				// section bottom passes the viewport bottom before the last dock
				// even closes) → park as soon as the dock releases and let
				// parkRunway below hand out the runway. This used to split the
				// leftover travel — (lastP + 1) / 2 — which is not a position, just
				// a midpoint: on this page it landed at 94% of the section, by which
				// time the column bottom (where the parked final frame sits) had
				// risen to ~200px from the viewport top, so three quarters of the
				// parked product was off screen and the ghost took off from a rect
				// nobody could see. It also made the whole ending sit on a cliff —
				// 30px of layout drift flipped pDone between the two branches and
				// moved the park by 300px (2026-07-25).
				if (!(pDone > lastP)) pDone = lastP;
				// Give the reversal at least parkRunway px of scroll, never past the
				// section. NOTE this is a floor that runs PAST the preferred point,
				// so it must stay smaller than the right column's lead-out (see
				// --svs-right-lead-out) — every px of runway beyond the lead-out is a
				// px the parked product spends above the viewport top.
				if (this.parkRunway) pDone = Math.max(pDone, Math.min(1, lastP + this.parkRunway / total));
				pDone = Math.min(1, pDone);
				this.parkProgress = pDone;
				// Document scroll position of pDone: p = (vh - rect.top) / total
				// and rect.top = sectionDocTop - scrollY.
				this.parkScrollY = pDone * total - viewportHeight + (sectionRect.top + window.scrollY);
				points.push({ p: pDone, v: 1 });
				points.push({ p: 1, v: 1 });
				// Park with the FRAME BOTTOM on the window bottom, not at offset
				// 0. The asset stages its ending at the strip bottom, and under
				// the old rendering (frame scaled into the column, frame height
				// == column height) offset 0 showed exactly that. The clipping
				// window broke the equality: the natural-height strip runs ~1000px
				// past the column, so returning to 0 slid the settling product
				// down through the window's bottom edge mid-ending ("on the way down it gets
				// cut off") and parked it below the section — where the ghost
				// flight then took off from, invisible under the next module
				// (2026-07-20). min(0, …) keeps short-enough strips exactly where
				// the author framed them, i.e. the old geometry is the 0 case.
				parkOffset = Math.min(0, leftBox.height - (videoTop - leftTop) - videoHeight);
				this.parkOffset = parkOffset;
				offsets.push({ p: pDone, v: parkOffset });
				// Hand the product over to section-handoff's ghost image and get
				// out of the way. pDone is exactly where that flight takes off,
				// so from here the ghost is the product the visitor sees and the
				// strip is redundant — worse than redundant now that the footage
				// sits on a SOLID backdrop: past this point the strip overhangs
				// the section and its opaque background paints over the next
				// module's headline. The alpha footage this section was built
				// for had no such body, which is why the curve never faded out.
				// Scrub runs both ways, so scrolling back up fades it in again.
				// It's not a "transition duration", it's "one notch of scrolling". At pDone the ghost image takes off from the product in the frozen frame,
				// and the two coincide at that moment; scroll a bit further and it flies away while the strip stays put
				// fading out — two products sit side by side for as long as the fade-out travel lasts. A 0.04 travel on this site
				// is ≈190px, and the "ghosting" the user reported (2026-07-25) was exactly those 190px. Use 36px:
				// enough to avoid flicker from a hard cut, short enough that a second product can't be seen.
				// 2026-07-26: shrunk to 1px = **hard cut**. Two products sit side by side for as long as the fade-out travel lasts —
				// 36px under slow scrolling (60px/s) is still 0.6 seconds of two products, and the user still reported "ghosting".
				// The hard cut used to flicker because the ghost image's takeoff geometry was approximated by two hand-tuned percentages
				// (handoff_src_width / handoff_src_offset), with a 15.5% size error and a 19px position error,
				// on top of 126px from a stale measure() snapshot. Now section-handoff's src has been changed to be
				// **read live at the moment of takeoff + derived from footage calibration constants (footageProductBottomFrac /
				// footageProductHeightFrac)**; measured top-edge and height differences at the takeoff point are both 0px,
				// the two coincide exactly, so the hard cut is invisible.
				// ⚠️ If that derivation is ever changed back to hand-tuned percentages, this must be restored to a ~36px fade-out,
				// otherwise the misalignment will surface as hard-cut flicker.
				const HANDOFF_FADE = 1 / total;
				opacities.push({ p: pDone, v: 1 });
				opacities.push({ p: Math.min(1, pDone + HANDOFF_FADE), v: 0 });
				opacities.push({ p: 1, v: 0 });
			} else {
				// Geometric mode holds the last dock's frame to p=1 — it is
				// parked from that dock onward, so a follower has nothing to
				// wait for.
				this.parkProgress = null;
				this.parkScrollY = null;
				this.parkOffset = null;
				const lastDockV = points.length > 1 ? points[points.length - 1].v : Math.min(1, this.dockMax * timeScale);
				points.push({ p: 1, v: lastDockV });
			}
			// Entry shows the strip where the author framed it (offset 0 at
			// p=0); exit holds the park offset so the ending never slides back
			// out of the window. Nothing paints over the neighbours: the frame
			// bottom sits on the column bottom, and the strip is faded out past
			// pDone anyway.
			offsets.push({ p: 1, v: parkOffset });

			// What drives showing/hiding the dock static images = the frame-freeze bands of the three docks + the final park band.
			// The final segment isn't in docks[] (docks are derived from the right-column videos); its flat segment is bounded by
			// points.push({p: pDone, v: 1}) / ({p: 1, v: 1}) above, i.e. [parkProgress, 1].
			this.holdBands = docks.map((dock) => ({ start: dock.pStart, end: dock.holdEnd }));
			if (this.parkProgress != null) {
				this.holdBands.push({ start: this.parkProgress, end: 1 });
			}

			// Force monotonicity so the video never plays backwards while the page
			// scrolls forwards. (The offset map is exempt — it legitimately moves
			// both ways.)
			for (let i = 1; i < points.length; i++) {
				if (points[i].p < points[i - 1].p) points[i].p = points[i - 1].p;
				if (points[i].v < points[i - 1].v) points[i].v = points[i - 1].v;
			}
			this.mapPoints = points;
			this.offsetPoints = this.dockManifest && docks.length ? offsets : null;
			// The two geometric quantities used by the clamp, refreshed on layout re-measure (reading rects every frame is too expensive)
			this.viewfinderH = leftBox.height;
			this.stripFrameH = videoHeight;
			// Velocity table for frame-grid quantization margin compensation. It uses the **viewfinder** height (videoHeight, the same frame of reference
			// as the product positions in the manifest), not the column height. Rebuilt on every re-measure: when the product size
			// changes, the on-screen displacement per frame changes with it. duration has to wait for loadedmetadata, and that
			// event itself triggers another layout (see the listener in the constructor), so failing to get
			// the duration here is only temporarily null and needs no extra remedy.
			// Geometric mode never reaches the hand-off branch above, so it has
			// no endpoint of its own — and without one the curve would sit at
			// its opening value (0) forever, hiding the strip outright. It also
			// has no ghost to hand over to, so it simply stays visible.
			if (!opacities.some((point) => point.p >= 1)) {
				opacities.push({ p: 1, v: 1 });
			}
			this.opacityPoints = this.offsetPoints ? opacities : null;
			this.playGates = playGates.size ? playGates : null;
			this.popWindows = popWindows.length ? popWindows : null;
			this.capWindows = capWindows.length ? capWindows : null;
			// parkScrollY only settles once the video's intrinsic size is known
			// (the fit math needs it), which can land after a follower has
			// already measured. Announce every remeasure so section-handoff can
			// re-read it instead of caching a value from before metadata.
			if (typeof CustomEvent === "function") {
				this.dispatchEvent(new CustomEvent("scroll-video-split:measured", { bubbles: true }));
			}
			if (!this.offsetPoints && this.appliedOffset) {
				this.appliedOffset = 0;
				this.scrubVideo.style.transform = "";
				// Clear the layer along with it, otherwise it keeps the previous layout's translateY and drifts out of step with the video
				if (this.holdsLayer) {
					this.holdsLayer.style.transform = "";
				}
			}
			if (!this.opacityPoints && this.appliedOpacity !== 1) {
				this.appliedOpacity = 1;
				this.scrubVideo.style.opacity = "";
				if (this.holdsLayer) {
					this.holdsLayer.style.opacity = "";
				}
			}

			// Re-link dock-bound annotations (data-dock-index="1"-based): their
			// vertical anchor and visibility window derive from the measured
			// dock instead of hand-tuned percentages, so they can never drift
			// out of alignment with the artwork.
			this.annotations.forEach((annotation) => {
				const index = parseInt(annotation.el.dataset.dockIndex, 10);
				annotation.dock = index >= 1 && docks[index - 1] ? docks[index - 1] : null;
				if (annotation.dock) {
					// data-dock-offset nudges the anchor away from the artwork
					// center (% of the displayed artwork height, negative = up)
					// so the ring can sit on a specific part — e.g. a button at
					// the top edge — instead of mid-body.
					const offset = (parseFloat(annotation.el.dataset.dockOffset) || 0) / 100;
					const y = annotation.dock.anchor + offset * (videoHeight / leftBox.height);
					annotation.el.style.setProperty("--svs-anno-y", (y * 100).toFixed(3) + "%");
					// Glow-anchored docks also pin the horizontal position (the
					// element's own --svs-anno-x from the block's x setting is
					// overridden — inline setProperty wins over the Liquid style)
					// and publish the breathing glow's bloom diameter. The CSS
					// derives every light layer (bloom, sonar pings, core dot) from
					// --svs-anno-glow, so the whole cluster scales with the strip
					// across viewports and fit modes. --svs-anno-glow (22% of strip
					// width) is the outer BLOOM; the bright core dot is 0.245× that,
					// so its radius is ~0.12×the bloom diameter. The leader beam
					// ends at the core's edge (gap = 0.12×diameter) so it points at
					// the glow centre without stabbing through it.
					if (annotation.dock.anchorX != null) {
						const glowPx = 0.22 * videoWidth;
						annotation.el.style.setProperty("--svs-anno-x", (annotation.dock.anchorX * 100).toFixed(3) + "%");
						annotation.el.style.setProperty("--svs-anno-glow", glowPx.toFixed(1) + "px");
						annotation.el.style.setProperty("--svs-anno-gap", (glowPx * 0.12).toFixed(1) + "px");
					}
					annotation.el.style.setProperty("--svs-anno-line", annotation.line + "px");
				}
			});
			this.guardAnnotationOverlap(items);
			this.guardAnnotationEdges();
		}

		/* Bubbles must not be cut off by the viewport (2026-07-24).
		   A bubble is centered on its anchor (left:0 + translateX(-50%)), with its width determined by the copy and **not scaled with the
		   product** — about 350px is typical. So whenever the anchor is less than half a bubble width from either side of the screen, the text gets
		   cut; it actually happened once (after the composition shifted left as a whole, "Long-press" was cut to "ong-press").
		   Narrow desktops + long copy + anchors near the edge can also reproduce it, independent of that particular composition.

		   What's fixed is the bubble rather than the size solve: counting the bubble width into the product-height solve would mean letting a fixed-width
		   piece of text squeeze the product size, and on narrow screens a big chunk of the product would be eaten. Here we only measure how much it overflows and push it back
		   horizontally by that much, without giving up any product size. As a result the bubble and leader line are no longer coaxial — a form this section
		   already allows (the -88% variant of text_side:left is off-center).

		   Before clamping, reset nudge to zero and then measure: what's measured must be the position "without correction", otherwise every
		   measureDocks would correct on top of the previous correction, and the bubble would drift away. */
		guardAnnotationEdges() {
			if (!this.annotations.length || !this.isDesktop) return;
			// Same number as EDGE in layout(): the section's 20px left/right gutter.
			const EDGE = 20;
			const vw = this.viewportWidth();
			this.annotations.forEach((annotation) => {
				const label = annotation.el.querySelector(".scroll-video-split__annotation-label");
				if (!label) return;
				label.style.setProperty("--svs-anno-nudge", "0px");
				const rect = label.getBoundingClientRect();
				if (!rect.width) return;
				let nudge = 0;
				if (rect.left < EDGE) {
					nudge = EDGE - rect.left;
				} else if (rect.right > vw - EDGE) {
					nudge = vw - EDGE - rect.right;
				}
				// When both sides overflow at once (the bubble is wider than the viewport), don't push: whichever way it's pushed it still gets cut,
				// and staying centered at least cuts both ends equally.
				if (nudge && rect.width <= vw - 2 * EDGE) {
					label.style.setProperty("--svs-anno-nudge", nudge.toFixed(1) + "px");
				}
			});
		}

		/* The pill parks well ABOVE its dock: the glow sits ~6% of the strip's
		   height above the product (≈220px at a 1250px grid), and the leader
		   line plus the pill add ~120px more — while the copy block above is
		   only (item height/2 + item gap) = 38vh away. Below ~895px of viewport
		   height the two overlap, which is why it read as "fine on my monitor,
		   broken on my laptop" (2026-07-20).

		   Two levers, both driven by MEASURED rects rather than by that
		   arithmetic, and both applied only where a pill actually collides:
		   1. shorten the leader line (the pill drops toward the glow, nothing
		      in the layout moves) down to MIN_LINE;
		   2. whatever deficit is left pushes the item itself down via
		      --svs-anno-clearance, then re-measures once so every dock offset
		      and anchor lands in the new coordinates.
		   The horizontal test matters: pills over an interlude of copy (which
		   spans the whole grid) collide, pills over the previous CLIP normally
		   do not — that one is centred in the right column, far from the left
		   column's glow — and those must keep their full leader line. */
		guardAnnotationOverlap(items) {
			if (!this.annotations.length) return;
			// Floor on the leader line. Deliberately generous: the line is the
			// only thing tying the pill to the glow, and squeezing it to a stub
			// (the old 24px) read as "the orange line is too short" even though
			// nothing overlapped. Anything the shortening can no longer absorb
			// falls through to --svs-anno-clearance, which pushes the item down
			// instead — trading a little vertical space for a readable leader.
			const MIN_LINE = 56;
			// Clears the pill's 4px float bob with a little air to spare.
			const MIN_GAP = 12;
			const clearance = new Map();
			this.annotations.forEach((annotation) => {
				const index = parseInt(annotation.el.dataset.dockIndex, 10);
				const item = annotation.dock && index >= 1 ? items[index - 1] : null;
				const prev = item ? this.stageOf(item).previousElementSibling : null;
				const label = annotation.el.querySelector(".scroll-video-split__annotation-label");
				if (!prev || !label) return;
				const pill = label.getBoundingClientRect();
				const above = this.outerBox(prev);
				if (pill.right <= above.left || pill.left >= above.right) return;
				const deficit = above.bottom + MIN_GAP - pill.top;
				if (deficit <= 0) return;
				const line = Math.max(MIN_LINE, annotation.line - deficit);
				annotation.el.style.setProperty("--svs-anno-line", line.toFixed(1) + "px");
				const rest = deficit - (annotation.line - line);
				if (rest > 0.5) clearance.set(item, Math.max(clearance.get(item) || 0, rest));
			});
			if (this.clearancePass || !clearance.size) return;
			clearance.forEach((px, item) =>
				this.stageOf(item).style.setProperty("--svs-anno-clearance", Math.ceil(px) + "px"),
			);
			// Single re-measure, guarded against recursion: pass two keeps the
			// clearance and re-reads everything with it applied. It cannot ask
			// for more, because the margin it just added is exactly the space
			// the pill was missing.
			this.clearancePass = true;
			try {
				this.measureDocks();
			} finally {
				this.clearancePass = false;
			}
		}

		// Box of an element INCLUDING children that overflow it horizontally.
		// The copy block's own box is the right column, but its heading and
		// body are widened past it (see --svs-text-frame in the CSS) and
		// centred, so its rect alone would under-report the width by hundreds
		// of pixels — exactly the strip the pill sits over.
		outerBox(el) {
			const rect = el.getBoundingClientRect();
			let left = rect.left;
			let right = rect.right;
			let top = rect.top;
			let bottom = rect.bottom;
			Array.from(el.children).forEach((child) => {
				const box = child.getBoundingClientRect();
				if (box.width <= 0 && box.height <= 0) return;
				left = Math.min(left, box.left);
				right = Math.max(right, box.right);
				top = Math.min(top, box.top);
				bottom = Math.max(bottom, box.bottom);
			});
			return { left: left, right: right, top: top, bottom: bottom };
		}

		// Piecewise-linear lookup. Travel segments are deliberately linear:
		// sine easing was tried and removed — near each dock its slope tends
		// to zero, so the artwork advances a frame only every few hundred ms
		// (a slow-crawl zone that reads as stutter right where the columns
		// align). The soft settle into a dock comes from the seeker's
		// exponential smoothing instead.
		mapProgress(rawProgress) {
			const points = this.mapPoints;
			if (!points) return rawProgress;
			return this.lookup(points, rawProgress);
		}

		// Whether this loop video's start is deferred to its dock's show window.
		// Before the first measurement (playGates null) every loop in manifest
		// mode counts as gated — an early start would break the sequencing the
		// gate exists for; the tick loop starts it as soon as gates land.
		gatedLoop(video) {
			// Mobile: never gate — there is no scrub tick to release the gate, so
			// every clip must loop freely on visibility. Read live so an editor
			// viewport toggle to mobile ungates already-observed clips.
			if (!this.mqDesktop.matches) {
				return false;
			}
			if (!this.dockManifest) {
				return false;
			}
			return !this.playGates || this.playGates.has(video);
		}

		// Shared by the progress map ({p, v: time fraction}) and the offset map
		// ({p, v: px}) — both are piecewise-linear over the same breakpoints.
		// With ease=true, use smoothstep within a segment instead of linear interpolation: velocity goes to zero at both ends.
		// Only the OFFSET mapping turns it on. The offset is flat within each dock window (velocity 0); linear
		// interpolation makes the velocity jump to full the moment a window ends — especially obvious at the last dock, where the ending pulls the strip
		// back 306px at about 0.8px per px of scroll, and the frozen frame instantly shoots off in the opposite direction, reading as
		// "bounces up once and only then falls" (2026-07-22). The amount of offset hasn't changed; what changes is how it
		// starts moving. The time mapping must never be eased along with it — that would change the scrub's frame-advance rhythm and run into the baseline's
		// rule that "position-driven, one continuous and one discrete, inevitably saw-tooths".
		lookup(points, rawProgress, ease) {
			for (let i = 1; i < points.length; i++) {
				if (rawProgress <= points[i].p) {
					const a = points[i - 1];
					const b = points[i];
					if (b.v === a.v) return a.v;
					if (b.p === a.p) return b.v;
					let f = (rawProgress - a.p) / (b.p - a.p);
					if (ease) f = f * f * (3 - 2 * f);
					return a.v + (b.v - a.v) * f;
				}
			}
			return points[points.length - 1].v;
		}

		// On-screen diagnostics for the handoff script (see __handoffPeek in section-handoff.js): rendered into
		// the [v2] floating box, so a single screenshot carries the state of both files.
		peekHandoff() {
			try {
				const peek = window.__handoffPeek;
				if (!peek) return "script not running";
				const state = peek();
				return typeof state === "string" ? state : JSON.stringify(state);
			} catch (error) {
				return "err:" + error.message;
			}
		}

		// Ratcheted pull-out (see the constructor for why it ratchets). The
		// transform resolves to NONE at the end, so the landing position is the
		// untransformed flow box — i.e. exactly the spot the size model solved for
		// "both products equal and centred as a pair, whole section on one screen".
		// This animation can move the item on the way in; it can never move where
		// it comes to rest.
		applyPopOut(rawProgress) {
			if (!this.popWindows || this.reducedMotion) {
				return;
			}
			this.popWindows.forEach((win, i) => {
				const span = Math.max(1e-6, win.end - win.start);
				const raw = Math.min(1, Math.max(0, (rawProgress - win.start) / span));
				const seen = Math.max(this.popSeen[i] || 0, raw);
				// Landed and already cleared: stop writing styles every frame.
				if (seen >= 1 && this.popSeen[i] >= 1) {
					return;
				}
				this.popSeen[i] = seen;
				const t = 1 - Math.pow(1 - seen, 3);
				if (t >= 1) {
					win.item.style.transform = "";
					win.item.style.filter = "";
					win.item.style.opacity = "";
					return;
				}
				// blur() lands on a PAUSED clip: playGates only starts it at
				// showStart, which is where this finishes. Blurring a still frame is
				// cheap; blurring a decoding video every frame would not be.
				win.item.style.transform = "translateX(" + (win.dx * (1 - t)).toFixed(1) + "px)";
				win.item.style.filter = "blur(" + (this.popBlur * (1 - t)).toFixed(2) + "px)";
				win.item.style.opacity = Math.min(1, t / this.popFade).toFixed(3);
			});
		}

		// Caption track "pull-out": once past the trigger point it runs to completion **on its own** over capRevealMs, independent of scroll speed.
		// Being the opposite of pull-out (scroll-driven) is deliberate; see the capRevealMs comment in the constructor for why.
		// At the end all inline styles are cleared, and the landing spot is still the untransformed flow box — this animation only affects
		// "how it comes in", never "where it stops" (the equal-height centered position solved by the size model).
		triggerCaptionReveal(rawProgress) {
			if (!this.capWindows) {
				return;
			}
			this.capWindows.forEach((win, i) => {
				// The criterion's meaning is "the phone **has actually appeared on screen**" (see the capCanReveal comment).
				// Both cases count:
				//   · HAVE_CURRENT_DATA: the decoder has a frame to paint;
				//   · has a poster: the fallback cover is standing in, so the phone is likewise already painted on screen.
				// ⚠️ Before posters were added to the right column on 2026-07-27, checking only readyState was **equivalent** — without decoding
				// nothing was shown. With posters that equivalence broke: the phone appears first via the poster while
				// readyState is still 0, yet the gate judged "the video hasn't appeared yet", so the captions were slow to come out (user feedback:
				// "the right video came out, but the progress bar didn't come out with it"). The criterion must return to its original meaning.
				const ready = !!(win.video && (win.video.readyState >= 2 || win.video.getAttribute("poster")));
				if (!capCanReveal({ p: rawProgress, at: win.at, fired: win.fired, ready: ready })) {
					return;
				}
				win.fired = true;
				// reducedMotion: the same gate (progress + video has a frame), just visible immediately without the animation.
				// This branch used to bail out entirely, so the captions were always visible — the invariant simply didn't hold on it.
				if (this.reducedMotion) {
					win.el.style.transition = "";
					win.el.style.transform = "";
					win.el.style.opacity = "";
					win.el.dataset.svsCapRevealed = "1";
					this.capDone[i] = true;
					return;
				}
				// Write the starting state again here and force a reflow: measure and the first tick may land in the same frame
				// (e.g. loading from the middle of the page), in which case the browser never painted the "stuffed behind the phone" state,
				// the transition wouldn't happen, and the captions would just flash in.
				win.el.style.transition = "none";
				win.el.style.transform = "translateX(" + win.dx.toFixed(1) + "px)";
				win.el.style.opacity = "0";
				void win.el.offsetWidth;
				win.el.style.transition =
					"transform " +
					this.capRevealMs +
					"ms cubic-bezier(0.2, 0.8, 0.2, 1), opacity " +
					Math.round(this.capRevealMs * 0.55) +
					"ms linear";
				win.el.style.transform = "translateX(0px)";
				win.el.style.opacity = "1";
				const timer = setTimeout(() => {
					this.capTimers.delete(timer);
					win.el.style.transition = "";
					win.el.style.transform = "";
					win.el.style.opacity = "";
					// The dataset marker survives across measures: resize re-measures, and without it already-placed
					// captions would be stuffed back behind the phone and replayed.
					win.el.dataset.svsCapRevealed = "1";
					this.capDone[i] = true;
				}, this.capRevealMs);
				this.capTimers.add(timer);
			});
		}

		clearPopOut() {
			this.popWindows = null;
			this.popSeen = [];
			this.querySelectorAll(".scroll-video-split__item").forEach((item) => {
				item.style.transform = "";
				item.style.filter = "";
				item.style.opacity = "";
			});
			this.capWindows = null;
			this.capDone = [];
			this.capTimers.forEach((timer) => clearTimeout(timer));
			this.capTimers.clear();
			this.querySelectorAll(".svs-steps").forEach((el) => {
				el.style.transition = "";
				el.style.transform = "";
				el.style.opacity = "";
				delete el.dataset.svsCapRevealed;
			});
		}

		tick(now) {
			if (!this.active) {
				return;
			}
			const dt = this.lastTickTime ? Math.min(100, now - this.lastTickTime) : 16.7;
			this.lastTickTime = now;

			const rawProgress = this.progress();
			// The single smoothing point in the whole module. Both the frame and the flush offset sample from here — synchronization is guaranteed by construction,
			// the two no longer lag separately, so they can't misalign and push the product out of the viewfinder.
			this.smoothProgress = advanceProgress(rawProgress, this.smoothProgress, dt, this.smoothMs);
			const mappedProgress = this.mapProgress(this.smoothProgress);

			// Dock static images only stand in while hi-res isn't ready: during loading, a user who scrolls to a dock point **stares at it**,
			// and low-res can't hold up there (the 2026-07-25 "very blurry" complaint came from staring at a frozen frame).
			// Once hi-res arrives they're permanently disabled, and the steady state is exactly the same as today.
			// smoothProgress may be null on the first frame; pass -1 so it's guaranteed to fall outside all bands.
			if (this.videoTier !== "hi") {
				// When there's no video at all, stand one in outside bands too (otherwise the right column's poster is always there while the left column is empty,
				// asymmetric); once low-res arrives, only show inside dock bands and hand motion segments back to the video.
				const hp = this.smoothProgress == null ? -1 : this.smoothProgress;
				const noVideo = this.videoTier === "none";
				const idx = holdStillIndex(hp, this.holdBands, noVideo);
				this.showHoldStill(idx);
				// Remember "which band this frame's fallback image came from". Dock images are **whole-canvas** images with the product's
				// height in the canvas welded in, while translateY decides which band of the canvas the viewfinder aims at — the two must
				// share a source. Outside a band, pairing a dock's image with the offset interpolated from current progress means pairing content A with content B's
				// offset, and the product gets pushed out of the viewfinder (measured: at progress 0.095 the window aims at ~10% of the canvas, while
				// dock1's product is at 32.3%, entirely outside the window). Below, that dock's offset is taken accordingly.
				this.holdFrozenBand =
					idx >= 0 && holdBandIndex(hp, this.holdBands) < 0 && this.holdBands ? this.holdBands[idx] : null;
			} else if (this.shownHold !== -1) {
				this.showHoldStill(-1);
			}
			this.applyPopOut(rawProgress);
			this.triggerCaptionReveal(rawProgress);

			if (this.debugReadout) {
				const ct = this.scrubVideo.currentTime || 0;
				// v2 tag + the hand-off numbers. The tag is a version probe: if
				// the overlay still shows only two lines, the preview is running
				// stale JS and nothing else here can be trusted.
				this.debugReadout.textContent =
					"[v2] currentTime=" +
					ct.toFixed(3) +
					"s\nprogress=" +
					rawProgress.toFixed(3) +
					"\npDone=" +
					(this.parkProgress == null ? "-" : this.parkProgress.toFixed(3)) +
					"\nstrip opacity=" +
					(this.opacityPoints ? this.appliedOpacity.toFixed(3) : "curve disabled") +
					"\nparkOffset=" +
					(this.parkOffset == null ? "-" : Math.round(this.parkOffset)) +
					"\nhandoff=" +
					this.peekHandoff();
				// Stamp each clip's state onto its outline label: whether the
				// engine ever loaded it, whether the decoder has a frame to
				// paint (readyState) and whether it is running. A clip sitting
				// in the viewport with loaded=- or rs=0 explains a blank screen
				// that the layout numbers say should be full.
				if (this.debugOutline) {
					this.loopVideos.forEach((video) => {
						const item = video.parentElement;
						if (!item || !video.classList.contains("scroll-video-split__feature")) {
							return;
						}
						item.dataset.svsState =
							"loaded" +
							(video.dataset.loaded ? "√" : "✗") +
							" rs" +
							video.readyState +
							" " +
							(video.paused ? "paused" : "playing") +
							" " +
							Math.round(video.getBoundingClientRect().top);
					});
				}
			}

			// HAVE_CURRENT_DATA: never seek while the video is still fetching.
			if (this.duration && this.scrubVideo.readyState >= 2) {
				// Leave a small tail so we never hit "ended".
				const target = mappedProgress * Math.max(0, this.duration - 0.05);
				this.seeker.update(target, dt);
			}

			// Manifest mode levelling offset (compositor-only translateY; the
			// strip's background is transparent, so shifting it just moves the
			// product). Inside a dock window the value is constant — combined
			// with the held still frame, the product is pixel-frozen there.
			if (this.offsetPoints) {
				// Sampled from the same source as the frame (same smoothProgress, same set of p breakpoints), so the two are naturally in sync:
				//   · no filter of its own → no snapping → no jumps during slow scrolling (old symptom A)
				//   · within flat segments lookup returns the segment value as-is → parkOffset exact → the product isn't flat-cut by the viewfinder
				//     (old symptom B, and also the "parks exactly at parkOffset" that section-handoff relies on)
				// For the investigation history and the common origin of the two symptoms, see tests/unit/svs-offset-sync.test.mjs.
				// The interpolation must be **the same kind** as the frame's (both linear, no ease). Offset = dock target − the product's position
				// in the frame; the product moves at constant speed within a segment and the frame time is a linear function of progress, so the geometrically correct offset varies
				// linearly with progress. smoothstep would deviate from the correct value mid-segment by up to 9.6% of the segment span (entry fall segment Δ−530px
				// → ±51px), and make velocity go to zero at breakpoints and bulge mid-segment, putting kinks in the composite path (the kink in the 2026-07-26 user
				// feedback; it disappeared after turning off the offset with ?svs=nolevel, which is how it was located).
				// In the fallback state (no video at all, a dock image standing in outside a band), the offset must take **the same
				// dock**'s breakpoint value, not the interpolation at current progress — see the long comment at holdFrozenBand
				// above. ⚠️ This is not "adding offset compensation to the strip" (the 2026-07-25 approach introduced regressions of "two products
				// appearing at once + the product's bottom getting clipped", all of which were reverted): no new offset is invented here,
				// it just takes the value the engine itself already computed at that breakpoint.
				const offsetProgress = this.holdFrozenBand ? this.holdFrozenBand.start : this.smoothProgress;
				this.smoothOffset = this.lookup(this.offsetPoints, offsetProgress);
				// Hard geometric floor: the strip may not sit lower than the position where "the product's bottom edge stays inside the viewfinder". The mapping only guarantees breakpoints
				// are correct; linear interpolation between them isn't in sync with the product's actual movement in the footage — mid-travel in the last segment the product
				// has already dropped to 98.4% of the frame while the strip hasn't risen enough, and gets flat-cut by the viewfinder (measured overrun 7~8px).
				// Neither dock points nor parkOffset trigger it, so the precision of flushing and handoff is unaffected.
				// ⚠️ The fallback state (no video at all, a dock image standing in) must **skip** this clamp: its ct
				// comes from the video's currentTime, and at that point there's no video at all and ct is always 0, so it computes the floor as if "the product is at
				// the top of the canvas" and pushes the already-frozen dock offset askew — measured: product too low, cut off by the viewport,
				// annotations detached from the product. Yet another mismatch of "the content is the dock's image but the parameters come from a nonexistent video".
				// The comment above itself shows skipping is safe: dock-point offsets never trigger the clamp anyway.
				const offsetPx = this.holdFrozenBand
					? this.smoothOffset
					: clampOffsetToViewfinder({
							ty: this.smoothOffset,
							bottomFrac: productBottomFrac({
								manifest: this.dockManifest,
								duration: this.duration,
								// Take whichever of the two has the product lower (bottomFrac rises monotonically with ct): clamping a bit more on forward jumps is harmless,
								// but on backward jumps it must clamp for the old frame still on screen, otherwise it cuts.
								ct: Math.max(this.scrubVideo.currentTime || 0, this.presentedTime == null ? 0 : this.presentedTime),
							}),
							boxHeight: this.viewfinderH,
							frameHeight: this.stripFrameH,
						});
				// Threshold/decimal places must be fine enough: the offset now moves a small step each frame, and a coarse threshold would re-quantize it into stair steps.
				if (Math.abs(offsetPx - this.appliedOffset) > 0.02) {
					this.appliedOffset = offsetPx;
					const tf = "translateY(" + offsetPx.toFixed(2) + "px)";
					this.scrubVideo.style.transform = tf;
					// Same value, written in one place — computing it separately in two places would inevitably drift, and the image and video would misalign
					if (this.holdsLayer) {
						this.holdsLayer.style.transform = tf;
					}
				}
			}
			if (this.opacityPoints) {
				const opacity = resolveStripOpacity({
					curve: this.lookup(this.opacityPoints, rawProgress),
					p: rawProgress,
					park: this.parkProgress,
					armed: this.dataset.handoffArmed === "1",
				});
				if (Math.abs(opacity - this.appliedOpacity) > 0.005) {
					this.appliedOpacity = opacity;
					const op = opacity >= 0.995 ? "" : opacity.toFixed(3);
					this.scrubVideo.style.opacity = op;
					// During the exit fade-out the image layer must fade along with it, otherwise an opaque product image would show at the handoff
					if (this.holdsLayer) {
						this.holdsLayer.style.opacity = op;
					}
				}
			} else if (this.appliedOpacity !== 1) {
				this.appliedOpacity = 1;
				this.scrubVideo.style.opacity = "";
				if (this.holdsLayer) {
					this.holdsLayer.style.opacity = "";
				}
			}

			// Gated loop starts: the observer armed the video (wantsPlay) when
			// it became visible enough; the actual start waits here for the
			// dock's show window — the annotation appears, THEN the loop moves.
			// Scrolling back above the window pauses and rewinds symmetrically.
			if (this.playGates && !this.reducedMotion) {
				for (const [video, gate] of this.playGates) {
					if (!video.dataset.wantsPlay) {
						continue;
					}
					// Until the caption pull-out finishes the gate is shut tight; once done (capDone set) it falls back to the moment the annotation appears,
					// so scrolling back up and down again means "play as soon as the annotation appears". See the pickGateStart comment.
					const start = pickGateStart(gate, this.capDone);
					if (rawProgress >= start) {
						if (!video.dataset.gateStarted && video.src) {
							video.dataset.gateStarted = "1";
							video.currentTime = 0;
							this.gateTimers.set(
								video,
								setTimeout(() => {
									this.gateTimers.delete(video);
									// Re-check: the gate may have closed while waiting.
									if (video.dataset.gateStarted) {
										video.play().catch(() => {});
									}
								}, GATE_PLAY_DELAY_MS),
							);
						}
					} else if (video.dataset.gateStarted) {
						delete video.dataset.gateStarted;
						this.clearGateTimer(video);
						video.pause();
						if (video.readyState >= 1) {
							video.currentTime = 0;
						}
					}
				}
			}

			// Dock-bound annotations show while their dock holds AND the paired
			// right video is fully on screen (show window computed in
			// measureDocks) — outside it the artwork is travelling or the right
			// video is cut off, and a visible callout would point at nothing (a
			// "linger" extension was tried and removed for exactly that
			// misalignment; want longer reading time → widen the dock window,
			// which extends alignment and callout together). Manual ones key
			// off the mapped video progress so they track what the frame shows.
			const percent = mappedProgress * 100;
			for (const annotation of this.annotations) {
				const visible = annotation.dock
					? rawProgress >= annotation.dock.showStart && rawProgress <= annotation.dock.showEnd
					: percent >= annotation.showAt && percent < annotation.hideAt;
				if (visible !== annotation.visible) {
					annotation.visible = visible;
					annotation.el.classList.toggle("is-visible", visible);
				}
			}

			this.rafId = requestAnimationFrame(this.tick);
		}

		/* ---------- right column: threshold-triggered loop videos ---------- */

		setupLoops() {
			// Attach sources ahead of time (2 screens) so playback can start the
			// moment the threshold is crossed.
			this.lazyObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							this.videosIn(entry.target).forEach((video) => this.loadLoop(video));
							this.lazyObserver.unobserve(entry.target);
						}
					});
				},
				{ rootMargin: "200% 0px 200% 0px" },
			);

			// Two thresholds = hysteresis: start at `threshold`, stop only after
			// dropping 10% of the viewport below it. A single boundary made a
			// gentle scroll hovering at the line fire restart-seek + play /
			// pause + reset over and over — a hitch right where the item
			// appears.
			const stopAt = Math.max(0, this.threshold - 0.1);
			this.playObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						this.videosIn(entry.target).forEach((video) => {
							if (entry.intersectionRatio >= this.threshold - 0.005) {
								if (video.dataset.wantsPlay) {
									return;
								}
								video.dataset.wantsPlay = "1";
								this.loadLoop(video);
								// Manifest mode holds the actual start for the dock's
								// show window (annotation-first sequencing) — the tick
								// loop fires it. Gate even before playGates is measured:
								// starting early once would break the sequence.
								if (this.gatedLoop(video)) {
									return;
								}
								if (!this.reducedMotion && video.src) {
									video.currentTime = 0;
									video.play().catch(() => {});
								}
							} else if (entry.intersectionRatio <= stopAt + 0.005) {
								delete video.dataset.wantsPlay;
								delete video.dataset.gateStarted;
								this.clearGateTimer(video);
								if (video.src) {
									video.pause();
									if (video.readyState >= 1) {
										video.currentTime = 0;
									}
								}
							}
							// Between the two thresholds: keep whatever state we're in.
						});
					});
				},
				{ threshold: [this.threshold, stopAt] },
			);

			// Observe the ITEM, not the video. On desktop the item is a
			// viewfinder with overflow: hidden and the frame is ~1.7x wider than
			// it, so a good third of the video element is always clipped — on a
			// tablet-width column, most of it. intersectionRatio is measured
			// against the element's FULL box (clipping included), so the 30%
			// threshold became unreachable and the clips would silently never
			// play. The item's box is exactly what the visitor sees, which is
			// what the threshold was always meant to describe.
			this.loopItems = Array.from(this.querySelectorAll(".scroll-video-split__item"));
			this.loopItems.forEach((item) => {
				this.videosIn(item).forEach((video) => this.ensureAspect(video, item));
				this.lazyObserver.observe(item);
				this.playObserver.observe(item);
			});
		}

		// The loop clips inside one item that THIS viewport actually uses.
		// Desktop hides the paired phone clip (mobile_video_*) outright, and
		// loading it there would burn bandwidth on an invisible video and let
		// its metadata overwrite the item's --svs-aspect — which the entire
		// desktop size model is derived from. Live matchMedia so an editor
		// viewport switch picks up the other set without a teardown.
		videosIn(item) {
			const all = Array.from(item.querySelectorAll(".scroll-video-split__loop"));
			return this.mqDesktop.matches
				? all.filter((video) => video.classList.contains("scroll-video-split__feature"))
				: all;
		}

		loadLoop(video) {
			if (video.dataset.loaded) {
				return;
			}
			video.dataset.loaded = "1";
			const hiUrl = video.dataset.src || null;
			const lowUrl = video.dataset.srcLow || null;
			if (!hiUrl && !lowUrl) {
				return;
			}
			this.handles.push(
				this.core.progressiveAttach(video, {
					lowUrl: lowUrl,
					hiUrl: hiUrl,
					// Download while playing: the loop videos only play forward and the only seek is back to 0 (always buffered),
					// so they don't need to be fully in hand like the left-column strip. These three videos are 2.3~4.4MB on the Shopify CDN,
					// and fully downloading takes 13.7s / 33.7s each, during which the element shows nothing.
					streaming: true,
					onReady: () => {
						// The threshold may have been crossed before the source was
						// ready — honor the pending intent now. Gated loops wait for
						// the tick loop instead (it retries once src exists).
						if (video.dataset.wantsPlay && !this.reducedMotion && !this.gatedLoop(video)) {
							video.currentTime = 0;
							video.play().catch(() => {});
						}
					},
				}),
			);
		}
	}
	customElements.define("scroll-video-split", ScrollVideoSplit);
}

const gating = {
	pickGateStart,
	unitWidthCeiling,
	bandHeightCeiling,
	headerInnerBand,
	advanceProgress,
	productBottomFrac,
	clampOffsetToViewfinder,
	resolveStripOpacity,
	holdBandIndex,
	holdStillIndex,
	shouldSwapNow,
	capCanReveal,
	orphanCaptions,
	entryLiftOffset,
};
if (typeof module === "object" && module && module.exports) {
	module.exports = gating;
}
if (typeof globalThis !== "undefined") {
	globalThis.SVSGating = gating;
}
